import React from 'react';
import { create } from 'zustand';
import apiClient from '../lib/api';
import toast from 'react-hot-toast';

// ─────────────────────────────────────────────────────────────────
// 1. Queue / Team Schema
// ─────────────────────────────────────────────────────────────────
export interface SupportQueue {
  id: string;
  key: string;
  label: string;
  code?: string;
  queue_id?: number | string;
  team_id?: number | string;
  team_key?: string;
  team_name?: string;
  name?: string;
  queue_key?: string;
  queue_label?: string;
  description?: string;
  manager_name?: string;
  email_routing?: string;
  email_address?: string;
  emailAddress?: string;
  configuredEmail?: string;
  configured_email?: string;
  emailSource?: 'dedicated' | 'queue_account' | 'primary_infra' | string;
  email_source?: 'dedicated' | 'queue_account' | 'primary_infra' | string;
  server_mode?: string;
  serverMode?: string;
  default_sla?: string;
  defaultSla?: string;
  thread_matching_enabled?: boolean;
  threadMatchingEnabled?: boolean;
  members_count?: number;
  membersCount?: number;
  active_tickets_count?: number;
  active_tickets?: number;
  activeTickets?: number;
  sla_tier?: 'Standard' | 'Premium' | 'VIP' | string;
  dedicated_smtp_host?: string;
  dedicated_smtp_port?: string;
  dedicated_smtp_user?: string;
  dedicated_smtp_pass?: string;
  dedicated_smtp_security?: string;
  dedicated_imap_host?: string;
  dedicated_imap_port?: string;
  dedicated_imap_user?: string;
  dedicated_imap_pass?: string;
  dedicated_imap_security?: string;
  dedicatedSmtpHost?: string;
  dedicatedSmtpPort?: string;
  dedicatedSmtpUser?: string;
  dedicatedSmtpPass?: string;
  dedicatedSmtpSecurity?: string;
  dedicatedImapHost?: string;
  dedicatedImapPort?: string;
  dedicatedImapUser?: string;
  dedicatedImapPass?: string;
  dedicatedImapSecurity?: string;
  created_at?: string;
  updated_at?: string;
}

export type SupportTeam = SupportQueue;

export interface AwsSesEmailSettings {
  provider: 'aws_ses';
  region: string;
  send_mode: 'smtp' | 'api';
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_password_set: boolean;
  aws_access_key_id: string;
  aws_secret_access_key_set: boolean;
  sender_email: string;
  configuration_set: string;
  inbound_enabled: boolean;
  inbound_method: 'sns_webhook' | 's3';
  inbound_s3_bucket: string;
  inbound_sns_topic_arn: string;
  updated_at?: string;
  updated_by?: string | null;
}

// ─────────────────────────────────────────────────────────────────
// 2. Agent Schema
// ─────────────────────────────────────────────────────────────────
export interface SupportAgent {
  id: string;
  first_name: string;
  last_name: string;
  name?: string;
  email: string;
  role: string;
  team_name?: string;
  team_key?: string;
  assigned_queues: string[];
  assigned_teams?: string[];
  status: 'Active' | 'On Break' | 'Offline' | string;
  phone?: string | null;
  avatar_url?: string | null;
  created_at?: string;
  updated_at?: string;
}

// ─────────────────────────────────────────────────────────────────
// 3. Auto-Assignment Rule Schema
// ─────────────────────────────────────────────────────────────────
export interface AutoRule {
  id: string | number;
  name: string;
  strategy: 'Round Robin' | 'Skill Match' | 'Least Loaded' | 'Direct Assign';
  queue_key: string;
  assignee_name: string;
  status: 'Active' | 'Paused';
  condition_field?: string;
  condition_value?: string;
  created_at?: string;
}

// ─────────────────────────────────────────────────────────────────
// 4. SLA Policy Schema
// ─────────────────────────────────────────────────────────────────
export interface SLAPolicy {
  id: string;
  priority: 'Urgent' | 'High' | 'Medium' | 'Low';
  response_time_mins: number;
  resolution_time_mins: number;
  business_hours_only: boolean;
  escalation_email: string;
  status: 'Active' | 'Inactive';
}

// ─────────────────────────────────────────────────────────────────
// 5. Ticket Numbering Schema
// ─────────────────────────────────────────────────────────────────
export interface NumberingConfig {
  prefix: string;
  padding_digits: number;
  next_number: number;
  include_year: boolean;
  separator: string;
}

// ─────────────────────────────────────────────────────────────────
// 6. Email Configuration Schema
// ─────────────────────────────────────────────────────────────────
export interface EmailConfig {
  provider: 'google' | 'aws_ses' | 'smtp_imap' | 'smtp';
  google_client_id: string;
  google_client_secret: string;
  google_delegation_email: string;
  google_authorized_scopes: string;
  aws_ses_region?: string;
  aws_ses_access_key?: string;
  aws_ses_secret_key?: string;
  aws_ses_sender_email?: string;
  aws_ses_config_set?: string;
  smtp_host: string;
  smtp_port: string;
  smtp_username: string;
  smtp_password?: string;
  smtp_security: 'TLS' | 'SSL' | 'STARTTLS' | 'NONE';
  inbound_imap_host?: string;
  inbound_imap_port?: string;
  inbound_imap_username?: string;
  inbound_imap_password?: string;
  inbound_imap_security?: 'SSL' | 'TLS' | 'STARTTLS' | 'NONE';
  status: 'Connected' | 'Not Configured' | 'Error';
}

// ─────────────────────────────────────────────────────────────────
// 7. Email & Signature Template Schema
// ─────────────────────────────────────────────────────────────────
export interface EmailTemplate {
  id: string;
  name: string;
  subject_template: string;
  body_template: string;
  type: 'Customer Response' | 'Internal Notification' | 'Satisfaction Survey' | 'Signature';
  variables: string[];
  is_default?: boolean;
  created_at?: string;
}

// ─────────────────────────────────────────────────────────────────
// 8. Database Configuration Schema
// ─────────────────────────────────────────────────────────────────
export interface DBConfig {
  id?: number | string;
  engine: 'PostgreSQL' | 'MySQL' | 'Custom Database' | string;
  host: string;
  port: string | number;
  database_name: string;
  username: string;
  password?: string;
  hasPassword?: boolean;
  ssl_mode?: 'disable' | 'allow' | 'prefer' | 'require' | 'verify-ca' | 'verify-full' | string;
  ssl_enabled?: boolean;
  max_pool?: number;
  pool_size?: number;
  idle_timeout_ms?: number;
  connection_timeout_ms?: number;
  is_primary?: boolean;
  is_active?: boolean;
  read_replica_host?: string;
  read_replica_port?: number | string;
  backup_schedule?: string;
  status?: string;
  latency_ms?: number;
  is_configured?: boolean;
  updated_at?: string;
}

// ─────────────────────────────────────────────────────────────────
// 9. Audit Log Event Schema
// ─────────────────────────────────────────────────────────────────
export interface AuditLogEvent {
  id: string;
  event_type: 'TICKET_CREATE' | 'TICKET_TRANSITION' | 'TICKET_ASSIGN' | 'SETTING_UPDATE' | 'RULE_EXECUTE' | 'MACRO_APPLY' | 'CALL_LOG' | 'SECURITY_EVENT';
  user_name: string;
  user_email: string;
  user_role: string;
  action: string;
  resource_type: string;
  resource_id?: string;
  details: string;
  ip_address: string;
  timestamp: string;
}

// ─────────────────────────────────────────────────────────────────
// 10. AWS S3 Cloud Storage Configuration Schema
// ─────────────────────────────────────────────────────────────────
export interface AwsS3StorageSettings {
  provider: 'aws_s3';
  region: string;
  bucket_name: string;
  key_prefix: string;
  endpoint_url?: string | null;
  force_path_style: boolean;
  aws_access_key_id?: string | null;
  aws_secret_access_key_set: boolean;
  use_iam_role: boolean;
  sse_mode: 'AES256' | 'aws:kms';
  kms_key_id?: string | null;
  presign_upload_expiry_seconds: number;
  presign_download_expiry_seconds: number;
  max_file_size_mb: number;
  allowed_mime_types: string[];
  mirror_ticket_json: boolean;
  updated_at?: string;
  updated_by?: string | null;
}

export interface SupportStorageConfig {
  provider: 's3' | 'aws_s3';
  s3_bucket: string;
  s3_region: string;
  s3_prefix: string;
  access_key_id?: string;
  secret_access_key?: string;
  hasSecretKey?: boolean;
  s3_endpoint?: string;
  force_path_style?: boolean;
  direct_upload_enabled: boolean;
  max_file_size_mb: number;
  allowed_file_types: string[];
  is_configured?: boolean;
  access_key_preview?: string;
  use_iam_role?: boolean;
  sse_mode?: 'AES256' | 'aws:kms';
  kms_key_id?: string;
  presign_upload_expiry_seconds?: number;
  presign_download_expiry_seconds?: number;
  mirror_ticket_json?: boolean;
}

// ─────────────────────────────────────────────────────────────────
// 11. Outbound Webhook Configuration Schema
// ─────────────────────────────────────────────────────────────────
export interface WebhookConfig {
  id: string;
  name: string;
  url: string;
  platform: 'google_chat' | 'slack' | 'discord' | 'generic' | 'email';
  direction?: 'outbound' | 'inbound' | 'both';
  enableInbound?: boolean;
  googleSpaceId?: string;
  googleApiKey?: string;
  googleApiToken?: string;
  events: string[];
  secret?: string;
  isActive: boolean;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface WebhookDeliveryLog {
  id: string;
  webhookId: string;
  webhookName: string;
  event: string;
  url: string;
  platform: string;
  statusCode: number;
  success: boolean;
  latencyMs: number;
  error?: string;
  payloadPreview?: string;
  deliveredAt: string;
}

// ─────────────────────────────────────────────────────────────────
// 12. Email Infrastructure & Account Schema
// ─────────────────────────────────────────────────────────────────
export interface SupportEmailAccount {
  id: string;
  name: string;
  email_address: string;
  email?: string;
  queue_key: string;
  provider: 'google' | 'aws_ses' | 'smtp_imap' | 'smtp';
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_pass?: string;
  smtp_secure: boolean;
  imap_host: string;
  imap_port: number;
  imap_user: string;
  imap_pass?: string;
  imap_tls: boolean;
  is_active: boolean;
  is_default: boolean;
  hasSecret?: boolean;
  hasSmtpSecret?: boolean;
  hasImapSecret?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface SupportEmailLog {
  id: string;
  message_id?: string;
  sender_email?: string;
  recipient_email?: string;
  subject?: string;
  status: 'received' | 'processed' | 'spam_rejected' | 'malware_quarantined' | 'delivered' | 'failed';
  action_taken?: 'created_ticket' | 'appended_message' | 'quarantined' | 'ignored';
  ticket_id?: string;
  error_details?: string;
  created_at: string;
}

export interface DnsRecordItem {
  type: string;
  name: string;
  value: string;
  ttl: string;
  purpose: string;
  instructions: string;
}

export interface DnsChecklistData {
  subdomain: string;
  mailHost: string;
  serverIp: string;
  records: DnsRecordItem[];
  instructions: string;
}

// ─────────────────────────────────────────────────────────────────
// 13. Dynamic Ticket Types & Fields (TicketTypesView)
// ─────────────────────────────────────────────────────────────────
export interface DynamicVisibilityRule {
  controllingFieldId: string;
  controllingFieldName: string;
  triggerValues: string[];
}

export interface TicketField {
  id: string;
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'number' | 'date' | 'user' | 'asset' | 'checkbox' | 'multiselect';
  group: 'Details' | 'Asset & Configuration' | 'Requester Info' | 'Resolution & Closure' | 'Custom';
  isMandatory: boolean;
  isVisibleAgent: boolean;
  isVisiblePortal: boolean;
  isReadOnlyPortal?: boolean;
  options?: string[];
  placeholder?: string;
  helpText?: string;
  dynamicVisibility?: DynamicVisibilityRule;
  isSystem?: boolean;
}

export interface TicketTypeConfig {
  id: string;
  name: string;
  code: string;
  icon: string;
  sla: string;
  defaultQueue: string;
  defaultPriority: string;
  description: string;
  status: 'Active' | 'Draft';
  prefix?: string;
  tagPrefix?: string;
  tagId?: string;
  tagCode?: string;
  paddingDigits?: number;
  nextSequence?: number;
  teamId?: string;
  teamName?: string;
  teamCode?: string;
  activeWorkflowId?: string;
  activeWorkflowName?: string;
  fields: TicketField[];
  workflows?: string[];
  defaultWorkflow?: string;
  enableAsset?: boolean;
  enableVendor?: boolean;
  enableSla?: boolean;
}

export interface MasterTicketTypeWorkflow {
  mappingId: string;
  tenantId: string;
  ticketTypeId: string;
  ticketTypeName: string;
  ticketTypeCode?: string;
  workflowId: string;
  workflowName: string;
  workflowCode?: string;
  isDefault: boolean;
  displayOrder: number;
  ticketTypeStatus?: string;
  workflowIsActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// ─────────────────────────────────────────────────────────────────
// 14. Ticket Actions & Outcome Configurations (ActionsView)
// ─────────────────────────────────────────────────────────────────
export type SystemUseType =
  | 'None (Standard Action)'
  | 'Email User'
  | 'Email Agent'
  | 'Create Child Ticket'
  | 'Create Parent Ticket'
  | 'Create Related Ticket'
  | 'Create Opportunity'
  | 'Create Appointment/Task'
  | 'Complete Appointment/Task'
  | 'Add note to Child Ticket(s)'
  | 'Email Child Ticket(s)'
  | 'Add note to Parent Ticket'
  | 'Update Original Ticket'
  | 'Create Quotation'
  | 'Create Sales Order'
  | 'Create Purchase Order'
  | 'Create Invoice'
  | 'Re-assign'
  | 'Log to Supplier'
  | 'Email Supplier'
  | 'Supplier Response'
  | 'Supplier Fixed'
  | 'Create Bug/Issue (Jira/DevOps)'
  | 'Remote Session Invitation'
  | 'Create Knowledge Base Article'
  | 'Link To Knowledge Base Article'
  | 'Cancel Approval Process'
  | 'Email Accepted Approvers'
  | 'Email All Approvers'
  | 'Open URL'
  | 'Send Webhook/Queue Integration Runbook'
  | 'Consign Items'
  | 'Generate Report'
  | 'Create a new Asset'
  | 'Link to another Ticket'
  | 'Reclose'
  | 'Add CRM Note'
  | 'Save as a new Template'
  | 'Bulk email primary Service subscribers'
  | 'Forward'
  | 'Call on Microsoft Teams';

export interface ActionFieldItem {
  id: string;
  label: string;
  key: string;
  type: 'text' | 'textarea' | 'select' | 'user' | 'time' | 'checkbox' | 'date';
  isMandatory: boolean;
  isReadOnly: boolean;
  options?: string[];
}

export interface TicketActionConfig {
  id: string;
  code?: string;
  outcomeDescription: string;
  buttonName: string;
  buttonIcon: string;
  actionGroup: 'Communication' | 'Hierarchy & Linking' | 'Escalation & Reassign' | 'Finance & Orders' | 'External & Automation' | 'Resolution & Closure' | string;
  systemUse: SystemUseType | string;
  statusAfterAction: string;
  isQuickAction: boolean;
  allowPortalUsers: boolean;
  showSlaHoldToggle: boolean;
  showHideFromEndUser: boolean;
  showMarkImportant: boolean;
  showFollowTicket: boolean;
  showCloseTicket: boolean;
  showTranslate: boolean;
  showSatisfactionSurvey: boolean;
  responseBehaviour: 'Mark Responded' | 'Default Behaviour' | 'Do not affect SLA' | string;
  hideFromTicketHeader: boolean;
  visibilityOnParent: 'Show always' | 'Inherit' | 'Hide';
  visibilityOnChild: 'Show always' | 'Inherit' | 'Hide';
  sendEmail: boolean;
  suppressAutoResponseHeader: boolean;
  makeFromAddressVisible: boolean;
  defaultEmailTemplate: string;
  autoApplyTicketTemplate: string;
  defaultTeam: string;
  defaultAssignee: string;
  putOnSlaHold: boolean;
  releaseFromSlaHold: boolean;
  fields?: ActionFieldItem[];
  requiredFields?: any[];
  allowedTicketTypes: string[];
  allowedWorkflows: string[];
  accessControl: Array<{
    targetType: 'Role' | 'Team' | 'Agent' | 'User';
    targetName: string;
    level: 'Read and Modify' | 'Owner' | 'Execute Only';
  }>;
  allowedRoles?: string[];
  allowedAgents?: string[];
  allowAdmins?: boolean;
  allowAgents?: boolean;
  allowManagers?: boolean;
  wireCount?: number;
  wires?: Array<{
    id: number;
    workflowId: number;
    workflowName: string;
    fromStageId: number;
    fromStageName: string;
    toStageId: number;
    toStageName: string;
  }>;
  isActive?: boolean;
  label?: string;
  category?: string;
  actionCategory?: string;
  requiresNote?: boolean;
  requiresComment?: boolean;
  requiresApproval?: boolean;
}

// ─────────────────────────────────────────────────────────────────
// 15. Visual Graph Extended Workflows (WorkflowsView)
// ─────────────────────────────────────────────────────────────────
export interface WorkflowStepAction {
  id: string;
  name: string;
  buttonName: string;
  actionType: 'agent_action' | 'automation_runbook' | 'timer_countdown' | 'webhook' | 'approval_gate';
  targetStepId: string;
  targetStepName?: string;
  icon?: string;
  buttonStyle: 'primary' | 'secondary' | 'warning' | 'success' | 'destructive' | 'outline';
  requiresNote?: boolean;
  requiresFieldVerification?: boolean;
  actionButtonId?: number | string;
  actionCode?: string;
  lineStyle?: 'solid' | 'dashed';
  requiresApproval?: boolean;
  autoTrigger?: 'none' | 'customer_reply' | 'after_days' | string;
  autoAfterDays?: number;
}

export interface WorkflowStep {
  id: string;
  name: string;
  type: 'initial' | 'in_progress' | 'pending' | 'approval' | 'resolved' | 'closed';
  description: string;
  actions: WorkflowStepAction[];
  workingHoursOnly?: boolean;
  slaTimeoutHours?: number;
  autoEscalateToStepId?: string;
  onEnterWebhook?: string;
}

export interface ExtendedWorkflow {
  id: string;
  name: string;
  code: string;
  ticketTypeId?: string;
  ticketType: string;
  ticketTypeCode?: string;
  teamId?: string;
  teamName?: string;
  teamCode?: string;
  tagPrefix?: string;
  allowedTicketTypes?: string[];
  description: string;
  isDefault: boolean;
  isActive: boolean;
  workingHoursSchedule: string;
  steps: WorkflowStep[];
  allowedTeams: string[];
}


// ─────────────────────────────────────────────────────────────────
// 17. Multi-Channel Notification Rules (NotificationsView)
// ─────────────────────────────────────────────────────────────────
export type EventTriggerType =
  | 'ticket_created'
  | 'ticket_assigned'
  | 'status_changed'
  | 'note_added_public'
  | 'note_added_internal'
  | 'customer_replied'
  | 'sla_warning'
  | 'sla_breached'
  | 'approval_requested'
  | 'ticket_resolved'
  | 'ticket_closed'
  | 'vip_customer_activity'
  | 'asset_warranty_expiring'
  | 'unassigned_warning'
  | 'inactivity_warning'
  | 'urgent_priority_warning';

export type WarningSeverity = 'info' | 'low' | 'warning' | 'urgent' | 'critical';

export type DeliveryChannel = 'email' | 'in_app' | 'sound' | 'chat_webhook' | 'sms';

export interface NotificationRule {
  id: string;
  name: string;
  eventTrigger: EventTriggerType;
  description: string;
  isEnabled: boolean;
  isSystemDefault?: boolean;
  scope: 'global' | 'personal';
  ownerAgentEmail?: string;
  warningSeverity?: WarningSeverity;
  warningThresholdValue?: number;
  warningThresholdUnit?: 'minutes' | 'hours' | 'days' | 'percent_sla';
  autoEscalate?: boolean;
  channels: {
    email: boolean;
    in_app: boolean;
    sound: boolean;
    chat_webhook: boolean;
    sms: boolean;
  };
  criteria: {
    ticketTypes: string[];
    queues: string[];
    priorities: string[];
    categories: string[];
    vipOnly?: boolean;
    workingHoursOnly?: boolean;
  };
  recipients: {
    requester: boolean;
    assignedAgent: boolean;
    queueMembers: boolean;
    reportingManager: boolean;
    ticketWatchers: boolean;
    customEmails?: string[];
  };
  template: {
    subject: string;
    bodyText: string;
    chatCardTitle?: string;
    soundChime?: 'standard_chime' | 'urgent_siren' | 'subtle_ping';
  };
}

export interface PersonalAgentPreference {
  agentEmail: string;
  agentName: string;
  subscribedRuleIds: string[];
  optedOutChannelMap: Record<string, { email?: boolean; in_app?: boolean; sound?: boolean; chat_webhook?: boolean }>;
}

// ─────────────────────────────────────────────────────────────────
export const INITIAL_QUEUES: SupportQueue[] = [
  { id: '1', team_id: '1', key: 'support', team_key: 'support', queue_key: 'support', label: 'Support Team', team_name: 'Support Team', name: 'Support Team', description: 'Front-line end-user support, service requests and asset provisioning', manager_name: '', email_routing: 'girikumaran@trustybytes.in', email_address: 'girikumaran@trustybytes.in', emailAddress: 'girikumaran@trustybytes.in', emailSource: 'primary_infra', configuredEmail: '', server_mode: 'primary_system', default_sla: 'Default SLA Policy', members_count: 0, active_tickets_count: 0, sla_tier: 'Standard' },
  { id: '2', team_id: '2', key: 'administration', team_key: 'administration', queue_key: 'administration', label: 'Administration', team_name: 'Administration', name: 'Administration', description: 'Purchases, facilities, office operations, travel and expense claims', manager_name: '', email_routing: '', email_address: '', emailAddress: '', emailSource: 'none', configuredEmail: '', server_mode: 'primary_system', default_sla: 'Default SLA Policy', members_count: 0, active_tickets_count: 0, sla_tier: 'Standard' },
  { id: '3', team_id: '3', key: 'software', team_key: 'software', queue_key: 'software', label: 'Software Team', team_name: 'Software Team', name: 'Software Team', description: 'Software engineering, bug remediation, product features and deployments', manager_name: '', email_routing: '', email_address: '', emailAddress: '', emailSource: 'none', configuredEmail: '', server_mode: 'primary_system', default_sla: 'Default SLA Policy', members_count: 0, active_tickets_count: 0, sla_tier: 'Standard' },
  { id: '4', team_id: '4', key: 'hr', team_key: 'hr', queue_key: 'hr', label: 'HR Team', team_name: 'HR Team', name: 'HR Team', description: 'People operations, employee leave, onboarding, offboarding and grievances', manager_name: '', email_routing: '', email_address: '', emailAddress: '', emailSource: 'none', configuredEmail: '', server_mode: 'primary_system', default_sla: 'Default SLA Policy', members_count: 0, active_tickets_count: 0, sla_tier: 'Standard' },
  { id: '5', team_id: '5', key: 'supplier', team_key: 'supplier', queue_key: 'supplier', label: 'Supplier Team', team_name: 'Supplier Team', name: 'Supplier Team', description: 'External vendors, parts procurement, warranty claims and supplier deliveries', manager_name: '', email_routing: '', email_address: '', emailAddress: '', emailSource: 'none', configuredEmail: '', server_mode: 'primary_system', default_sla: 'Default SLA Policy', members_count: 0, active_tickets_count: 0, sla_tier: 'Standard' },
  { id: '6', team_id: '6', key: 'management', team_key: 'management', queue_key: 'management', label: 'Management Team', team_name: 'Management Team', name: 'Management Team', description: 'Executive approvals, capital budgeting, strategic proposals and policy exceptions', manager_name: '', email_routing: '', email_address: '', emailAddress: '', emailSource: 'none', configuredEmail: '', server_mode: 'primary_system', default_sla: 'Default SLA Policy', members_count: 0, active_tickets_count: 0, sla_tier: 'Standard' },
];

export const INITIAL_AGENTS: SupportAgent[] = [];

export const INITIAL_RULES: AutoRule[] = [];

export const INITIAL_SLA: SLAPolicy[] = [];

export const INITIAL_NUMBERING: NumberingConfig = {
  prefix: 'TB',
  padding_digits: 5,
  next_number: 1,
  include_year: false,
  separator: '#',
};

export const INITIAL_EMAIL_CONFIG: EmailConfig = {
  provider: 'smtp_imap',
  google_client_id: '',
  google_client_secret: '',
  google_delegation_email: '',
  google_authorized_scopes: '',
  smtp_host: '',
  smtp_port: '587',
  smtp_username: '',
  smtp_security: 'TLS',
  inbound_imap_host: '',
  inbound_imap_port: '993',
  inbound_imap_username: '',
  status: 'Not Configured',
};

export const INITIAL_TEMPLATES: EmailTemplate[] = [
  {
    id: 'tmpl-1',
    name: 'Standard Ticket Acknowledgement',
    type: 'Customer Response',
    subject_template: '[TB#{ticket_id}] We received your ticket: {subject}',
    body_template: 'Hi {requester_name},\n\nThank you for reaching out to TeamOne Support. Your ticket has been logged with ID TB#{ticket_id} and assigned to the {queue_name} queue.\n\nPriority: {priority}\nEstimated First Response: within {sla_time}\n\nBest regards,\nTeamOne IT Desk',
    variables: ['{ticket_id}', '{subject}', '{requester_name}', '{queue_name}', '{priority}', '{sla_time}'],
    is_default: true,
  },
];

export const INITIAL_DB_CONFIG: DBConfig = {
  engine: 'PostgreSQL',
  host: '',
  port: 5432,
  database_name: '',
  username: '',
  password: '',
  hasPassword: false,
  ssl_mode: 'prefer',
  max_pool: 20,
  idle_timeout_ms: 30000,
  connection_timeout_ms: 5000,
  is_primary: true,
  is_active: true,
  read_replica_host: '',
  read_replica_port: '',
  backup_schedule: 'daily',
  status: 'Not Configured',
  is_configured: false,
};

export const INITIAL_AUDIT_LOGS: AuditLogEvent[] = [];

export const INITIAL_SUPPORT_STORAGE: SupportStorageConfig = {
  provider: 's3',
  s3_bucket: '',
  s3_region: 'ap-south-1',
  s3_prefix: 'support/',
  access_key_id: '',
  secret_access_key: '',
  s3_endpoint: '',
  force_path_style: false,
  direct_upload_enabled: true,
  max_file_size_mb: 25,
  allowed_file_types: ['pdf', 'png', 'jpg', 'jpeg', 'zip', 'docx', 'xlsx', 'log', 'txt'],
  is_configured: false,
  access_key_preview: '',
  use_iam_role: false,
  sse_mode: 'AES256',
  kms_key_id: '',
  presign_upload_expiry_seconds: 300,
  presign_download_expiry_seconds: 300,
  mirror_ticket_json: true,
};

export const INITIAL_EXTENDED_WORKFLOWS: ExtendedWorkflow[] = [
  {
    id: 'wf-inc-standard',
    name: 'Standard Incident Management',
    code: 'WF-INC-01',
    ticketType: 'Incident',
    description: 'ITIL structured incident resolution lifecycle from triage through active investigation and closure.',
    isDefault: true,
    isActive: true,
    workingHoursSchedule: 'Mon-Fri 09:00 - 18:00 IST',
    allowedTeams: ['Support Team', 'L1 Helpdesk', 'L2 Engineering Support'],
    steps: [
      {
        id: 'step-inc-new',
        name: 'New',
        type: 'initial',
        description: 'New lifecycle stage',
        workingHoursOnly: true,
        slaTimeoutHours: 2,
        actions: [
          {
            id: 'act-inc-assign',
            name: 'Assign & Accept',
            buttonName: 'Assign & Accept',
            actionCode: 'ASSIGN_TICKET',
            actionType: 'agent_action',
            targetStepId: 'step-inc-assigned',
            targetStepName: 'Assigned',
            buttonStyle: 'primary',
          },
          {
            id: 'act-inc-ack',
            name: 'Send Acknowledgement',
            buttonName: 'Send Acknowledgement',
            actionCode: 'SEND_ACKNOWLEDGEMENT',
            actionType: 'agent_action',
            targetStepId: 'step-inc-assigned',
            targetStepName: 'Assigned',
            buttonStyle: 'secondary',
          },
          {
            id: 'act-inc-close-inv',
            name: 'Close as Invalid/Duplicate',
            buttonName: 'Close as Invalid/Duplicate',
            actionCode: 'CLOSE_INVALID',
            actionType: 'agent_action',
            targetStepId: 'step-inc-closed',
            targetStepName: 'Closed',
            buttonStyle: 'destructive',
            requiresNote: true,
          }
        ]
      },
      {
        id: 'step-inc-assigned',
        name: 'Assigned',
        type: 'in_progress',
        description: 'Assigned lifecycle stage',
        workingHoursOnly: true,
        slaTimeoutHours: 4,
        actions: [
          {
            id: 'act-inc-start',
            name: 'Start Work',
            buttonName: 'Start Work',
            actionCode: 'START_WORK',
            actionType: 'agent_action',
            targetStepId: 'step-inc-inprogress',
            targetStepName: 'In Progress',
            buttonStyle: 'primary',
          },
          {
            id: 'act-inc-hold',
            name: 'Put on Hold',
            buttonName: 'Put on Hold',
            actionCode: 'PUT_ON_HOLD',
            actionType: 'agent_action',
            targetStepId: 'step-inc-pending',
            targetStepName: 'Pending',
            buttonStyle: 'secondary',
            requiresNote: true,
          },
          {
            id: 'act-inc-log-sup',
            name: 'Log to Supplier',
            buttonName: 'Log to Supplier',
            actionCode: 'LOG_TO_SUPPLIER',
            actionType: 'agent_action',
            targetStepId: 'step-inc-supplier',
            targetStepName: 'With External Supplier',
            buttonStyle: 'secondary',
            requiresNote: true,
          }
        ]
      },
      {
        id: 'step-inc-inprogress',
        name: 'In Progress',
        type: 'in_progress',
        description: 'In Progress lifecycle stage',
        workingHoursOnly: true,
        slaTimeoutHours: 8,
        actions: [
          {
            id: 'act-inc-resolve',
            name: 'Resolve Ticket',
            buttonName: 'Resolve Ticket',
            actionCode: 'RESOLVE',
            actionType: 'agent_action',
            targetStepId: 'step-inc-resolved',
            targetStepName: 'Resolved',
            buttonStyle: 'primary',
            requiresNote: true,
          },
          {
            id: 'act-inc-hold-2',
            name: 'Put on Hold',
            buttonName: 'Put on Hold',
            actionCode: 'PUT_ON_HOLD',
            actionType: 'agent_action',
            targetStepId: 'step-inc-pending',
            targetStepName: 'Pending',
            buttonStyle: 'secondary',
            requiresNote: true,
          },
          {
            id: 'act-inc-log-sup-2',
            name: 'Log to Supplier',
            buttonName: 'Log to Supplier',
            actionCode: 'LOG_TO_SUPPLIER',
            actionType: 'agent_action',
            targetStepId: 'step-inc-supplier',
            targetStepName: 'With External Supplier',
            buttonStyle: 'secondary',
            requiresNote: true,
          }
        ]
      },
      {
        id: 'step-inc-pending',
        name: 'Pending',
        type: 'pending',
        description: 'Pending lifecycle stage',
        workingHoursOnly: false,
        slaTimeoutHours: 48,
        actions: [
          {
            id: 'act-inc-resume',
            name: 'Resume Work',
            buttonName: 'Resume Work',
            actionCode: 'RESUME_WORK',
            actionType: 'agent_action',
            targetStepId: 'step-inc-inprogress',
            targetStepName: 'In Progress',
            buttonStyle: 'primary',
          },
          {
            id: 'act-inc-close-pending',
            name: 'Close Ticket',
            buttonName: 'Close Ticket',
            actionCode: 'CLOSE',
            actionType: 'agent_action',
            targetStepId: 'step-inc-closed',
            targetStepName: 'Closed',
            buttonStyle: 'secondary',
            requiresNote: true,
          }
        ]
      },
      {
        id: 'step-inc-supplier',
        name: 'With External Supplier',
        type: 'pending',
        description: 'With External Supplier lifecycle stage',
        workingHoursOnly: true,
        slaTimeoutHours: 24,
        actions: [
          {
            id: 'act-inc-resume-sup',
            name: 'Resume Work',
            buttonName: 'Resume Work',
            actionCode: 'RESUME_WORK',
            actionType: 'agent_action',
            targetStepId: 'step-inc-inprogress',
            targetStepName: 'In Progress',
            buttonStyle: 'primary',
          },
          {
            id: 'act-inc-resolve-sup',
            name: 'Resolve Ticket',
            buttonName: 'Resolve Ticket',
            actionCode: 'RESOLVE',
            actionType: 'agent_action',
            targetStepId: 'step-inc-resolved',
            targetStepName: 'Resolved',
            buttonStyle: 'primary',
            requiresNote: true,
          },
          {
            id: 'act-inc-email-sup',
            name: 'Email Supplier',
            buttonName: 'Email Supplier',
            actionCode: 'EMAIL_SUPPLIER',
            actionType: 'agent_action',
            targetStepId: 'remain',
            targetStepName: 'With External Supplier',
            buttonStyle: 'secondary',
          }
        ]
      },
      {
        id: 'step-inc-resolved',
        name: 'Resolved',
        type: 'resolved',
        description: 'Resolved lifecycle stage',
        workingHoursOnly: true,
        slaTimeoutHours: 24,
        actions: [
          {
            id: 'act-inc-close-resolved',
            name: 'Close Ticket',
            buttonName: 'Close Ticket',
            actionCode: 'CLOSE',
            actionType: 'agent_action',
            targetStepId: 'step-inc-closed',
            targetStepName: 'Closed',
            buttonStyle: 'secondary',
            requiresNote: true,
          },
          {
            id: 'act-inc-reopen-resolved',
            name: 'Reopen Ticket',
            buttonName: 'Reopen Ticket',
            actionCode: 'REOPEN',
            actionType: 'agent_action',
            targetStepId: 'step-inc-inprogress',
            targetStepName: 'In Progress',
            buttonStyle: 'secondary',
            requiresNote: true,
          }
        ]
      },
      {
        id: 'step-inc-closed',
        name: 'Closed',
        type: 'closed',
        description: 'Closed lifecycle stage',
        actions: [
          {
            id: 'act-inc-reopen-closed',
            name: 'Reopen Ticket',
            buttonName: 'Reopen Ticket',
            actionCode: 'REOPEN',
            actionType: 'agent_action',
            targetStepId: 'step-inc-inprogress',
            targetStepName: 'In Progress',
            buttonStyle: 'secondary',
            requiresNote: true,
          }
        ]
      }
    ]
  },
  {
    id: 'wf-req-standard',
    name: 'Service Request Fulfillment',
    code: 'WF-REQ-02',
    ticketType: 'Service Request',
    description: 'Fulfillment process for hardware requests, software licenses, and access provisioning.',
    isDefault: true,
    isActive: true,
    workingHoursSchedule: 'Mon-Fri 09:00 - 18:00 IST',
    allowedTeams: ['IT Asset Management', 'Support Team'],
    steps: [
      {
        id: 'step-req-1',
        name: 'New / Approval Pending',
        type: 'approval',
        description: 'Request submitted, evaluating manager sign-off or cost approval.',
        workingHoursOnly: true,
        slaTimeoutHours: 24,
        actions: [
          {
            id: 'act-req-1',
            name: 'Approve Request',
            buttonName: 'Approve & Begin Fulfillment',
            actionType: 'agent_action',
            targetStepId: 'step-req-2',
            targetStepName: 'In Fulfillment / Procurement',
            buttonStyle: 'success',
          },
          {
            id: 'act-req-2',
            name: 'Reject Request',
            buttonName: 'Reject Request',
            actionType: 'agent_action',
            targetStepId: 'step-req-4',
            targetStepName: 'Closed / Completed',
            buttonStyle: 'destructive',
            requiresNote: true,
          }
        ]
      },
      {
        id: 'step-req-2',
        name: 'In Fulfillment / Procurement',
        type: 'in_progress',
        description: 'Provisioning license or hardware inventory from stock.',
        workingHoursOnly: true,
        slaTimeoutHours: 48,
        actions: [
          {
            id: 'act-req-3',
            name: 'Mark Fulfilled',
            buttonName: 'Fulfillment Complete',
            actionType: 'agent_action',
            targetStepId: 'step-req-3',
            targetStepName: 'Fulfilled / Ready for Handover',
            buttonStyle: 'success',
          }
        ]
      },
      {
        id: 'step-req-3',
        name: 'Fulfilled / Ready for Handover',
        type: 'resolved',
        description: 'Asset delivered and acknowledged by recipient.',
        actions: [
          {
            id: 'act-req-4',
            name: 'Close Request',
            buttonName: 'Complete & Close',
            actionType: 'agent_action',
            targetStepId: 'step-req-4',
            targetStepName: 'Closed / Completed',
            buttonStyle: 'primary',
          }
        ]
      },
      {
        id: 'step-req-4',
        name: 'Closed / Completed',
        type: 'closed',
        description: 'Completed request record.',
        actions: []
      }
    ]
  }
];

export const INITIAL_TICKET_TYPES: TicketTypeConfig[] = [];

export const INITIAL_TICKET_ACTIONS: TicketActionConfig[] = [];


export const INITIAL_NOTIFICATION_RULES: NotificationRule[] = [
  {
    id: 'notif-1',
    name: 'SLA Breach 30-Minute Warning Alert',
    eventTrigger: 'sla_warning',
    description: 'Dispatches high-priority chime and Google Chat notification when ticket SLA is nearing threshold.',
    isEnabled: true,
    isSystemDefault: true,
    scope: 'global',
    channels: {
      email: true,
      in_app: true,
      sound: true,
      chat_webhook: true,
      sms: false
    },
    criteria: {
      ticketTypes: ['Incident'],
      queues: ['support', 'engineering'],
      priorities: ['urgent', 'high'],
      categories: [],
      vipOnly: false,
      workingHoursOnly: false
    },
    recipients: {
      requester: false,
      assignedAgent: true,
      queueMembers: true,
      reportingManager: true,
      ticketWatchers: true
    },
    template: {
      subject: 'URGENT: Ticket #{ticket_id} is nearing SLA resolution breach!',
      bodyText: 'Ticket #{ticket_id} ({subject}) in queue {queue_name} has less than 30 minutes before SLA breach.',
      chatCardTitle: '⚠️ SLA Breach Warning Alert',
      soundChime: 'urgent_siren'
    }
  },
  {
    id: 'notif-2',
    name: 'New Ticket Customer Acknowledgement',
    eventTrigger: 'ticket_created',
    description: 'Sends automated receipt notification to customer when ticket is created.',
    isEnabled: true,
    isSystemDefault: true,
    scope: 'global',
    channels: {
      email: true,
      in_app: true,
      sound: false,
      chat_webhook: false,
      sms: false
    },
    criteria: {
      ticketTypes: ['Incident', 'Service Request'],
      queues: [],
      priorities: [],
      categories: []
    },
    recipients: {
      requester: true,
      assignedAgent: true,
      queueMembers: false,
      reportingManager: false,
      ticketWatchers: false
    },
    template: {
      subject: 'We have received your ticket: #{ticket_id}',
      bodyText: 'Hello {requester_name}, your request #{ticket_id} has been registered.',
      soundChime: 'standard_chime'
    }
  }
];

export const INITIAL_WEBHOOKS: WebhookConfig[] = [];

// ─────────────────────────────────────────────────────────────────
// State Interface
// ─────────────────────────────────────────────────────────────────
export interface SupportSettingsState {
  // 1. Queues
  queues: SupportQueue[];
  fetchQueues: () => Promise<void>;
  addQueue: (queue: Omit<SupportQueue, 'id' | 'created_at' | 'updated_at'>) => Promise<SupportQueue>;
  updateQueue: (id: string, updates: Partial<SupportQueue>) => Promise<void>;
  deleteQueue: (id: string) => Promise<void>;

  // 2. Agents
  agents: SupportAgent[];
  fetchAgents: () => Promise<void>;
  addAgent: (agent: Omit<SupportAgent, 'id' | 'created_at'>) => Promise<SupportAgent>;
  updateAgent: (id: string, updates: Partial<SupportAgent>) => Promise<SupportAgent | void>;
  deleteAgent: (id: string) => Promise<void>;

  // 3. Auto-Rules
  autoRules: AutoRule[];
  addRule: (rule: Omit<AutoRule, 'id'>) => Promise<AutoRule>;
  updateRule: (id: string | number, updates: Partial<AutoRule>) => Promise<void>;
  deleteRule: (id: string | number) => Promise<void>;

  // 4. SLA Policies
  slaPolicies: SLAPolicy[];
  fetchSlaPolicies: () => Promise<void>;
  addPolicy: (policy: Omit<SLAPolicy, 'id'>) => Promise<SLAPolicy>;
  updatePolicy: (id: string, updates: Partial<SLAPolicy>) => Promise<void>;
  deletePolicy: (id: string) => Promise<void>;

  // 5. Numbering
  numberingConfig: NumberingConfig;
  updateNumbering: (updates: Partial<NumberingConfig>) => Promise<void>;
  generateNextTicketId: () => string;

  // 6. Email Config
  emailConfig: EmailConfig;
  updateEmailConfig: (updates: Partial<EmailConfig>) => Promise<void>;
  testEmailConnection: () => Promise<boolean>;

  // 7. Templates
  emailTemplates: EmailTemplate[];
  addTemplate: (template: Omit<EmailTemplate, 'id'>) => Promise<EmailTemplate>;
  updateTemplate: (id: string, updates: Partial<EmailTemplate>) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;

  // 8. DB Config
  dbConfig: DBConfig;
  isTestingDatabase: boolean;
  databaseTestResult: { success: boolean; message: string; latencyMs?: number; version?: string; engine?: string } | null;
  updateDBConfig: (updates: Partial<DBConfig>) => void;
  fetchDatabaseConfig: () => Promise<void>;
  saveDatabaseConfig: (config?: Partial<DBConfig>) => Promise<void>;
  testDBConnection: (overrideConfig?: Partial<DBConfig>) => Promise<boolean>;

  // 9. Audit Logs
  auditLogs: AuditLogEvent[];
  logAuditEvent: (event: Omit<AuditLogEvent, 'id' | 'timestamp'>) => void;
  addAuditLog: (event: Omit<AuditLogEvent, 'id' | 'timestamp'>) => void;
  clearAuditLogs: () => void;

  // 10. AWS S3 Storage Config
  s3Settings: AwsS3StorageSettings | null;
  isLoadingS3Settings: boolean;
  isSavingS3Settings: boolean;
  isTestingS3Connection: boolean;
  s3TestResult: any;
  fetchS3Settings: () => Promise<AwsS3StorageSettings | null>;
  updateS3Settings: (data: Partial<AwsS3StorageSettings> & { aws_secret_access_key?: string }) => Promise<{ success: boolean; data?: AwsS3StorageSettings; message?: string }>;
  testS3Connection: (data?: any) => Promise<any>;
  storageConfig: SupportStorageConfig;
  isTestingStorage: boolean;
  storageTestResult: { success: boolean; message: string; latencyMs?: number; mode?: string; provider?: string } | null;
  updateStorageConfig: (updates: Partial<SupportStorageConfig>) => void;
  fetchStorageConfig: () => Promise<void>;
  saveStorageConfig: (config?: Partial<SupportStorageConfig>) => Promise<void>;
  testStorageConnection: () => Promise<{ success: boolean; message: string; latencyMs?: number; mode?: string }>;
  testMailuConnection: (config?: any) => Promise<any>;

  // 11. Outbound Webhooks
  webhooks: WebhookConfig[];
  webhookLogs: WebhookDeliveryLog[];
  isTestingWebhook: boolean;
  webhookTestResult: any;
  fetchWebhooks: () => Promise<void>;
  addWebhook: (data: Omit<WebhookConfig, 'id' | 'createdAt' | 'updatedAt'>) => Promise<WebhookConfig>;
  updateWebhook: (id: string, updates: Partial<WebhookConfig>) => Promise<void>;
  deleteWebhook: (id: string) => Promise<void>;
  fetchWebhookLogs: () => Promise<void>;
  testWebhookEndpoint: (webhook: Partial<WebhookConfig>, event?: string) => Promise<any>;
  triggerSampleWebhookEvent: (event: string, data?: any) => Promise<any>;

  // 12. Email Accounts & DNS Infrastructure
  emailAccounts: SupportEmailAccount[];
  emailProcessingLogs: SupportEmailLog[];
  dnsChecklist: DnsChecklistData | null;
  isTestingEmailInfra: boolean;
  emailInfraTestResult: any;
  isSyncingEmail: boolean;
  sesSettings: AwsSesEmailSettings | null;
  isLoadingSesSettings: boolean;
  isSavingSesSettings: boolean;
  isTestingSesConnection: boolean;
  fetchSesSettings: () => Promise<AwsSesEmailSettings | null>;
  updateSesSettings: (data: Partial<AwsSesEmailSettings> & { smtp_password?: string; aws_secret_access_key?: string }) => Promise<{ success: boolean; data?: AwsSesEmailSettings; message?: string }>;
  testSesConnection: (data?: any) => Promise<{ success: boolean; message: string }>;
  checkInboundMailNow: () => Promise<{ success: boolean; message: string; count?: number }>;
  fetchEmailAccounts: () => Promise<void>;
  saveEmailAccount: (account: Partial<SupportEmailAccount>) => Promise<SupportEmailAccount | null>;
  deleteEmailAccount: (id: string) => Promise<void>;
  syncEmailAccounts: (accountId?: string) => Promise<any>;
  fetchEmailLogs: (limit?: number) => Promise<void>;
  fetchDnsChecklist: (subdomain?: string, hostname?: string) => Promise<DnsChecklistData | null>;
  simulateIncomingEmail: (payload: { from: string; to: string; subject: string; text?: string; html?: string; headers?: Record<string, string>; attachments?: any[] }) => Promise<any>;
  testEmailInfraConnection: (config: { smtpHost?: string; smtpPort?: number; imapHost?: string; imapPort?: number }) => Promise<any>;

  // 13. Dynamic Ticket Types (TicketTypesView)
  ticketTypes: TicketTypeConfig[];
  isLoadingTicketTypes: boolean;
  fetchTicketTypes: () => Promise<void>;
  saveTicketType: (type: TicketTypeConfig) => Promise<TicketTypeConfig>;
  deleteTicketType: (id: string) => Promise<void>;

  // 13.1 Dynamic Categories, Impacts, Urgencies & End-Users from DB
  ticketCategories: Array<{ id: string; name: string; code?: string; description?: string }>;
  ticketImpacts: Array<{ id: string; name: string; code: string; level: number }>;
  ticketUrgencies: Array<{ id: string; name: string; code: string; level: number }>;
  endUsers: Array<{ id: string; employeeId?: string; name: string; email: string; phone?: string; department?: string; designation?: string }>;
  fetchCategories: () => Promise<void>;
  fetchImpacts: () => Promise<void>;
  fetchUrgencies: () => Promise<void>;
  fetchEndUsers: () => Promise<void>;

  // 14. Ticket Actions (ActionsView)
  ticketActions: TicketActionConfig[];
  isLoadingTicketActions: boolean;
  fetchTicketActions: () => Promise<void>;
  saveTicketAction: (action: TicketActionConfig) => Promise<TicketActionConfig>;
  deleteTicketAction: (id: string) => Promise<void>;

  // 15. Extended Workflows (WorkflowsView)
  extendedWorkflows: ExtendedWorkflow[];
  isLoadingWorkflows: boolean;
  fetchExtendedWorkflows: () => Promise<void>;
  saveExtendedWorkflow: (wf: ExtendedWorkflow) => Promise<ExtendedWorkflow>;
  deleteExtendedWorkflow: (id: string) => Promise<void>;
  deleteWorkflowStage: (workflowId: string, stageId: string) => Promise<void>;

  // 15.1 Normalized Master Relationships
  masterTicketTypeWorkflows: MasterTicketTypeWorkflow[];
  fetchMasterTicketTypeWorkflows: (ticketTypeId?: string) => Promise<MasterTicketTypeWorkflow[]>;

  // 17. Notification Rules & Preferences (NotificationsView)
  notificationRules: NotificationRule[];
  isLoadingNotificationRules: boolean;
  agentPreference: PersonalAgentPreference | null;
  fetchNotificationRules: () => Promise<void>;
  saveNotificationRule: (rule: NotificationRule) => Promise<NotificationRule>;
  deleteNotificationRule: (id: string) => Promise<void>;
  fetchAgentPreference: (email?: string) => Promise<PersonalAgentPreference>;
  saveAgentPreference: (pref: PersonalAgentPreference) => Promise<PersonalAgentPreference>;

  // 18. Mail Providers
  mailProviders: any[];
  fetchMailProviders: () => Promise<void>;
  saveMailProvider: (provider: any) => Promise<any>;

  // Global Reset
  resetAllSettingsToDefaults: () => void;
}

export function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function saveJson<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
  // Persist to server PostgreSQL database support.settings table
  // Filter out entity lists (queues, agents) which are managed in their own tables
  if (key === 'teamone_support_queues' || key === 'teamone_support_agents') return;
  apiClient.put(`/support/settings/general/${encodeURIComponent(key)}`, { value }).catch(() => {});
}

export async function syncSettingFromServer<T>(key: string, fallback: T): Promise<T> {
  try {
    const res = await apiClient.get(`/support/settings/general/${encodeURIComponent(key)}`);
    if (res.data?.data !== undefined && res.data?.data !== null) {
      const serverVal = res.data.data as T;
      try {
        localStorage.setItem(key, JSON.stringify(serverVal));
      } catch {}
      return serverVal;
    }
  } catch {
    // fallback
  }
  return loadJson<T>(key, fallback);
}

export function normalizeAgent(raw: any): SupportAgent {
  let queues: string[] = ['Support Team'];
  if (Array.isArray(raw.assigned_queues)) {
    queues = raw.assigned_queues;
  } else if (typeof raw.assigned_queues === 'string') {
    try {
      const parsed = JSON.parse(raw.assigned_queues);
      queues = Array.isArray(parsed) ? parsed : [raw.assigned_queues];
    } catch {
      queues = [raw.assigned_queues];
    }
  } else if (Array.isArray(raw.assignedQueues)) {
    queues = raw.assignedQueues;
  } else if (raw.assigned_queue) {
    queues = [raw.assigned_queue];
  }

  let teams: string[] = [];
  let primaryTeamKey = '';
  let primaryTeamName = '';

  if (Array.isArray(raw.teams)) {
    raw.teams.forEach((t: any) => {
      const code = String(t.teamCode || t.team_code || t.code || t.teamKey || t.key || '').toLowerCase();
      const name = String(t.teamName || t.team_name || t.name || '').toLowerCase();
      let k = code;
      if (code === 'sd' || code === 'support_desk' || name.includes('support')) k = 'support';
      else if (code === 'td' || code === 'technical_desk' || name.includes('technical')) k = 'technical';
      else if (code === 'sw' || name.includes('software')) k = 'software';
      else if (code === 'hr' || name.includes('hr')) k = 'hr';
      else if (code === 'mgt' || name.includes('management')) k = 'management';
      else if (code === 'sup' || name.includes('supplier')) k = 'supplier';
      if (k && !teams.includes(k)) teams.push(k);
      if (!primaryTeamKey) {
        primaryTeamKey = k;
        primaryTeamName = t.teamName || t.team_name || t.name || '';
      }
    });
  } else if (Array.isArray(raw.assigned_teams)) {
    teams = raw.assigned_teams;
  } else if (typeof raw.assigned_teams === 'string') {
    try {
      const parsed = JSON.parse(raw.assigned_teams);
      teams = Array.isArray(parsed) ? parsed : [raw.assigned_teams];
    } catch {
      teams = [raw.assigned_teams];
    }
  } else if (Array.isArray(raw.assignedTeams)) {
    teams = raw.assignedTeams;
  } else if (raw.team_key) {
    teams = [raw.team_key];
  }

  const defaultTeamKey = primaryTeamKey || raw.team_key || raw.teamKey || (teams.length > 0 ? teams[0] : 'support');
  const defaultTeamName = primaryTeamName || raw.team_name || raw.teamName || (defaultTeamKey === 'support' ? 'Support Team' : defaultTeamKey);
  const firstName = raw.first_name || raw.firstName || '';
  const lastName = raw.last_name || raw.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim() || raw.name || raw.email || raw.user_email || '';

  return {
    id: String(raw.id || raw.profile_id || ''),
    first_name: firstName,
    last_name: lastName,
    name: fullName,
    email: raw.email || raw.user_email || '',
    role: raw.role || raw.role_name || (raw.access_level === 'admin' ? 'Support Admin' : 'Support Agent'),
    team_name: defaultTeamName,
    team_key: defaultTeamKey,
    assigned_queues: queues.length > 0 ? queues : [defaultTeamName],
    assigned_teams: teams.length > 0 ? teams : [defaultTeamKey],
    status: raw.status || 'Active',
    phone: raw.phone || raw.contact_phone || '',
    avatar_url: raw.avatar_url || raw.avatarUrl || null,
    created_at: raw.created_at || raw.createdAt,
    updated_at: raw.updated_at || raw.updatedAt,
  };
}

const VALID_DB_TEAMS = new Set(['support', 'technical', 'software', 'management', 'supplier', 'hr']);
const loadInitialQueues = (): SupportQueue[] => {
  const loaded = loadJson<SupportQueue[]>('teamone_support_queues', []);
  if (!Array.isArray(loaded)) return [];
  return loaded.filter((q) => {
    const k = String(q.key || q.team_key || '').toLowerCase();
    const l = String(q.label || q.name || '').toLowerCase();
    return (
      VALID_DB_TEAMS.has(k) ||
      VALID_DB_TEAMS.has(k.replace(/-team|-desk|queue/g, '')) ||
      l.includes('support') ||
      l.includes('technical') ||
      l.includes('software') ||
      l.includes('management') ||
      l.includes('supplier') ||
      l.includes('hr')
    );
  });
};

const loadInitialAgents = (): SupportAgent[] => {
  const loaded = loadJson<SupportAgent[]>('teamone_support_agents', []);
  return Array.isArray(loaded) ? loaded : [];
};

export const useSupportSettingsStore = create<SupportSettingsState>((set, get) => ({
  queues: loadInitialQueues(),
  agents: loadInitialAgents(),
  autoRules: loadJson<AutoRule[]>('teamone_support_auto_rules', INITIAL_RULES),
  slaPolicies: loadJson<SLAPolicy[]>('teamone_support_sla_policies', INITIAL_SLA),
  numberingConfig: loadJson<NumberingConfig>('teamone_support_numbering_config', INITIAL_NUMBERING),
  emailConfig: loadJson<EmailConfig>('teamone_support_email_config', INITIAL_EMAIL_CONFIG),
  emailTemplates: loadJson<EmailTemplate[]>('teamone_support_email_templates', INITIAL_TEMPLATES),
  dbConfig: INITIAL_DB_CONFIG,
  isTestingDatabase: false,
  databaseTestResult: null,
  auditLogs: loadJson<AuditLogEvent[]>('teamone_support_audit_logs', INITIAL_AUDIT_LOGS),
  s3Settings: null,
  isLoadingS3Settings: false,
  isSavingS3Settings: false,
  isTestingS3Connection: false,
  s3TestResult: null,
  storageConfig: loadJson<SupportStorageConfig>('teamone_support_storage_config', INITIAL_SUPPORT_STORAGE),
  isTestingStorage: false,
  storageTestResult: null,
  webhooks: loadJson<WebhookConfig[]>('teamone_support_webhooks_config', INITIAL_WEBHOOKS),
  webhookLogs: [],
  isTestingWebhook: false,
  webhookTestResult: null,
  emailAccounts: [],
  emailProcessingLogs: [],
  dnsChecklist: null,
  isTestingEmailInfra: false,
  emailInfraTestResult: null,
  isSyncingEmail: false,
  sesSettings: null,
  isLoadingSesSettings: false,
  isSavingSesSettings: false,
  isTestingSesConnection: false,

  // New modules state
  ticketTypes: loadJson<TicketTypeConfig[]>('teamone_ticket_types_config', INITIAL_TICKET_TYPES),
  isLoadingTicketTypes: false,
  ticketActions: loadJson<TicketActionConfig[]>('teamone_ticket_actions_config', INITIAL_TICKET_ACTIONS),
  isLoadingTicketActions: false,
  extendedWorkflows: loadJson<ExtendedWorkflow[]>('teamone_extended_workflows', INITIAL_EXTENDED_WORKFLOWS),
  isLoadingWorkflows: false,
  notificationRules: loadJson<NotificationRule[]>('teamone_notification_rules_config', INITIAL_NOTIFICATION_RULES),
  isLoadingNotificationRules: false,
  agentPreference: null,
  mailProviders: [],
  ticketCategories: [],
  ticketImpacts: [],
  ticketUrgencies: [],
  endUsers: [],
  masterTicketTypeWorkflows: [],

  // --- QUEUES & TEAMS (Master Table from Database) ---
  fetchQueues: async () => {
    try {
      let res;
      try {
        res = await apiClient.get('/support/teams');
      } catch {
        try {
          res = await apiClient.get('/support/ticket-queues');
        } catch {
          res = await apiClient.get('/support/users/ticket-queues');
        }
      }
      const data = Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
      if (Array.isArray(data)) {
        const normalized: SupportQueue[] = data.map((q: any) => {
          let name = String(q.team_name || q.queue_label || q.label || q.name || '').replace(/\bDesk\b/gi, 'Team');

          let rawKey = String(q.team_key || q.queue_key || q.code || q.key || name.toLowerCase().replace(/[^a-z0-9]+/g, '-')).toLowerCase();
          let key = rawKey;
          if (rawKey === 'sd' || rawKey === 'support_desk' || name.toLowerCase().includes('support')) key = 'support';
          else if (rawKey === 'td' || rawKey === 'technical_desk' || name.toLowerCase().includes('technical')) key = 'technical';
          else if (rawKey === 'sw' || name.toLowerCase().includes('software')) key = 'software';
          else if (rawKey === 'sup' || name.toLowerCase().includes('supplier')) key = 'supplier';
          else if (rawKey === 'mgt' || name.toLowerCase().includes('management')) key = 'management';
          else if (rawKey === 'hr' || name.toLowerCase().includes('hr')) key = 'hr';
          else if (rawKey === 'adm' || rawKey === 'admin' || name.toLowerCase().includes('admin')) key = 'administration';

          // D8: id must always be the uuid — never the numeric PK.
          // The backend now returns uuid::text as id for /support/teams.
          const publicId = String(q.id || q.uuid || '');

          const rawEmail = (q.email_address || q.emailAddress || q.teamEmail || q.email_routing || '').trim();
          const configuredEmail = (q.configuredEmail || (rawEmail && !rawEmail.includes('@trustybytes.in') ? rawEmail : '') || '').trim();
          const effectiveEmail = rawEmail;
          const emailSource = q.emailSource || (configuredEmail ? 'dedicated' : (effectiveEmail ? 'primary_infra' : 'none'));

          return {
            id: publicId,
            key,
            label: name,
            display_order: q.display_order ?? q.displayOrder,
            displayOrder: q.display_order ?? q.displayOrder,
            // Preserve for legacy consumers that still read team_id — but set to uuid
            team_id: publicId,
            team_key: key,
            team_name: name,
            name: name,
            queue_key: key,
            queue_label: name,
            description: q.description || '',
            manager_name: q.manager_name || '',
            configuredEmail,
            configured_email: configuredEmail,
            emailSource,
            email_source: emailSource,
            email_routing: effectiveEmail,
            email_address: effectiveEmail,
            emailAddress: effectiveEmail,
            server_mode: q.server_mode || q.serverMode || 'primary_system',
            serverMode: q.server_mode || q.serverMode || 'primary_system',
            default_sla: q.default_sla || q.defaultSla || 'Default SLA Policy',
            defaultSla: q.default_sla || q.defaultSla || 'Default SLA Policy',
            thread_matching_enabled: q.thread_matching_enabled ?? true,
            threadMatchingEnabled: q.thread_matching_enabled ?? true,
            dedicated_smtp_host: q.dedicated_smtp_host || '',
            dedicated_smtp_port: q.dedicated_smtp_port || '587',
            dedicated_smtp_user: q.dedicated_smtp_user || '',
            dedicated_smtp_pass: q.dedicated_smtp_pass || '',
            dedicated_smtp_security: q.dedicated_smtp_security || 'TLS',
            dedicated_imap_host: q.dedicated_imap_host || '',
            dedicated_imap_port: q.dedicated_imap_port || '993',
            dedicated_imap_user: q.dedicated_imap_user || '',
            dedicated_imap_pass: q.dedicated_imap_pass || '',
            dedicated_imap_security: q.dedicated_imap_security || 'SSL',
            members_count: Number(q.members_count ?? q.membersCount ?? 0),
            active_tickets_count: q.active_tickets ?? q.active_tickets_count ?? q.activeTickets,
            active_tickets: q.active_tickets ?? q.active_tickets_count ?? q.activeTickets,
            sla_tier: (q.default_sla?.includes('VIP') ? 'VIP' : q.default_sla?.includes('Premium') ? 'Premium' : 'Standard') as any,
            created_at: q.created_at,
            updated_at: q.updated_at,
          };
        });

        const getRank = (key: string, label: string, order?: number) => {
          if (typeof order === 'number' && order > 0) return order;
          const k = (key || '').toLowerCase();
          const l = (label || '').toLowerCase();
          if (k === 'support' || l.includes('support') || k === 'sd') return 1;
          if (k === 'hr' || l.includes('hr')) return 2;
          if (k === 'administration' || k === 'adm' || l.includes('admin')) return 3;
          if (k === 'software' || l.includes('software') || k === 'sw') return 4;
          if (k === 'management' || l.includes('management') || k === 'mgt') return 5;
          if (k === 'supplier' || l.includes('supplier') || k === 'sup') return 6;
          return 99;
        };

        // Order teams by canonical hierarchy: Support -> HR -> Administration -> Software -> Management -> Supplier
        normalized.sort((a, b) => {
          const rankA = getRank(a.key, a.label, (a as any).displayOrder ?? (a as any).display_order);
          const rankB = getRank(b.key, b.label, (b as any).displayOrder ?? (b as any).display_order);
          if (rankA !== rankB) return rankA - rankB;
          return (a.label || '').localeCompare(b.label || '');
        });

        set({ queues: normalized });
        try {
          localStorage.setItem('teamone_support_queues', JSON.stringify(normalized));
        } catch {}
      }
    } catch (err) {
      console.error('Failed to fetch teams/queues in supportSettingsStore:', err);
    }
  },

  addQueue: async (data) => {
    try {
      const label = data.label || data.name || data.queue_label || data.team_name || '';
      const key = data.key || data.queue_key || data.team_key || '';
      const email = data.email_routing || data.email_address || data.emailAddress || '';
      const defaultSla = data.sla_tier === 'VIP' ? 'Enterprise VIP Tier' : data.sla_tier === 'Premium' ? 'Premium Business Tier' : (data.default_sla || data.defaultSla || 'Default SLA Policy');
      const payload = {
        label,
        queueKey: key,
        team_name: label,
        team_key: key,
        emailAddress: email,
        email_address: email,
        description: data.description || '',
        default_sla: defaultSla,
        defaultSla: defaultSla,
        serverMode: data.serverMode || data.server_mode || 'primary_system',
        server_mode: data.serverMode || data.server_mode || 'primary_system',
        threadMatchingEnabled: data.threadMatchingEnabled ?? data.thread_matching_enabled ?? true,
        thread_matching_enabled: data.threadMatchingEnabled ?? data.thread_matching_enabled ?? true,
        dedicatedSmtpHost: data.dedicatedSmtpHost || data.dedicated_smtp_host || '',
        dedicatedSmtpPort: data.dedicatedSmtpPort || data.dedicated_smtp_port || '587',
        dedicatedSmtpUser: data.dedicatedSmtpUser || data.dedicated_smtp_user || '',
        dedicatedSmtpPass: data.dedicatedSmtpPass || data.dedicated_smtp_pass || '',
        dedicatedSmtpSecurity: data.dedicatedSmtpSecurity || data.dedicated_smtp_security || 'TLS',
        dedicatedImapHost: data.dedicatedImapHost || data.dedicated_imap_host || '',
        dedicatedImapPort: data.dedicatedImapPort || data.dedicated_imap_port || '993',
        dedicatedImapUser: data.dedicatedImapUser || data.dedicated_imap_user || '',
        dedicatedImapPass: data.dedicatedImapPass || data.dedicated_imap_pass || '',
        dedicatedImapSecurity: data.dedicatedImapSecurity || data.dedicated_imap_security || 'SSL',
      };
      let res: any;
      try {
        res = await apiClient.post('/support/teams', payload);
      } catch (e1: any) {
        try {
          res = await apiClient.post('/support/users/ticket-queues', payload);
        } catch {
          res = await apiClient.post('/support/ticket-queues', payload);
        }
      }
      const q = res?.data?.data || res?.data || {};
      const newQueue: SupportQueue = {
        ...data,
        id: String(q.team_id || q.queue_id || Date.now()),
        team_id: q.team_id || q.queue_id,
        team_key: q.team_key || key,
        team_name: q.team_name || label,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      await get().fetchQueues();
      toast.success(`Team "${newQueue.label}" created`);
      return newQueue;
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to create team');
      throw err;
    }
  },

  updateQueue: async (id, updates) => {
    try {
      const label = updates.label || updates.name || updates.queue_label || updates.team_name;
      const rawEmail = updates.emailAddress !== undefined
        ? updates.emailAddress
        : (updates.email_address !== undefined
          ? updates.email_address
          : (updates.email_routing !== undefined ? updates.email_routing : undefined));
      const email = rawEmail !== undefined ? (rawEmail ? String(rawEmail).trim() : '') : '';
      const defaultSla = updates.default_sla || updates.defaultSla;
      const payload = {
        label,
        team_name: label,
        emailAddress: email,
        email_address: email,
        email_routing: email,
        default_sla: defaultSla,
        defaultSla: defaultSla,
        description: updates.description,
        serverMode: updates.serverMode || updates.server_mode,
        server_mode: updates.serverMode || updates.server_mode,
        threadMatchingEnabled: updates.threadMatchingEnabled ?? updates.thread_matching_enabled,
        thread_matching_enabled: updates.threadMatchingEnabled ?? updates.thread_matching_enabled,
        dedicatedSmtpHost: updates.dedicatedSmtpHost || updates.dedicated_smtp_host,
        dedicatedSmtpPort: updates.dedicatedSmtpPort || updates.dedicated_smtp_port,
        dedicatedSmtpUser: updates.dedicatedSmtpUser || updates.dedicated_smtp_user,
        dedicatedSmtpPass: updates.dedicatedSmtpPass || updates.dedicated_smtp_pass,
        dedicatedSmtpSecurity: updates.dedicatedSmtpSecurity || updates.dedicated_smtp_security,
        dedicatedImapHost: updates.dedicatedImapHost || updates.dedicated_imap_host,
        dedicatedImapPort: updates.dedicatedImapPort || updates.dedicated_imap_port,
        dedicatedImapUser: updates.dedicatedImapUser || updates.dedicated_imap_user,
        dedicatedImapPass: updates.dedicatedImapPass || updates.dedicated_imap_pass,
        dedicatedImapSecurity: updates.dedicatedImapSecurity || updates.dedicated_imap_security,
      };
      try {
        await apiClient.patch(`/support/teams/${id}`, payload);
      } catch (e1: any) {
        try {
          await apiClient.patch(`/support/users/ticket-queues/${id}`, payload);
        } catch {
          await apiClient.patch(`/support/ticket-queues/${id}`, payload);
        }
      }
      await get().fetchQueues();
      toast.success('Team updated successfully');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to update team');
      throw err;
    }
  },

  deleteQueue: async (id) => {
    try {
      try {
        await apiClient.delete(`/support/teams/${id}`);
      } catch (e1: any) {
        try {
          await apiClient.delete(`/support/users/ticket-queues/${id}`);
        } catch {
          await apiClient.delete(`/support/ticket-queues/${id}`);
        }
      }
      await get().fetchQueues();
      toast.success('Team removed');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to delete team');
      throw err;
    }
  },

  // --- AGENTS ---
  fetchAgents: async () => {
    try {
      const res = await apiClient.get('/support/agents', { params: { limit: 100 } });
      const raw = res.data?.data ?? res.data;
      const rawList = Array.isArray(raw) ? raw : (raw?.items ?? raw?.data ?? []);
      const normalized = rawList.map(normalizeAgent);
      set({ agents: normalized });
      try {
        localStorage.setItem('teamone_support_agents', JSON.stringify(normalized));
      } catch {}
    } catch (err) {
      console.warn('Failed to fetch agents in supportSettingsStore:', err);
    }
  },

  addAgent: async (data) => {
    try {
      const payload = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        role: data.role || 'Support Agent',
        assigned_queues: data.assigned_queues || ['Support Team'],
        assigned_teams: data.assigned_teams || ['support'],
        status: data.status || 'Active',
        phone: data.phone || null,
        team_key: data.team_key || 'support',
        team_name: data.team_name || 'Support Team',
      };
      const res = await apiClient.post('/support/agents', payload);
      const createdRaw = res.data?.data || res.data;
      const created = normalizeAgent(createdRaw || { ...payload, id: `usr-${Date.now()}`, created_at: new Date().toISOString() });
      const next = [...get().agents.filter((a) => String(a.id) !== String(created.id)), created];
      set({ agents: next });
      saveJson('teamone_support_agents', next);
      toast.success(`Agent "${created.first_name} ${created.last_name}" added successfully`);
      return created;
    } catch (err: any) {
      const newAgent: SupportAgent = normalizeAgent({
        ...data,
        id: `usr-${Date.now()}`,
        created_at: new Date().toISOString(),
      });
      const next = [...get().agents, newAgent];
      set({ agents: next });
      saveJson('teamone_support_agents', next);
      toast.success(`Agent "${newAgent.first_name} ${newAgent.last_name}" added`);
      return newAgent;
    }
  },

  updateAgent: async (id, updates) => {
    try {
      const res = await apiClient.patch(`/support/agents/${id}`, updates);
      const updatedRaw = res.data?.data || res.data;
      const existing = get().agents.find((a) => String(a.id) === String(id));
      const updated = normalizeAgent(updatedRaw || { ...existing, ...updates, id });
      const next = get().agents.map((a) => (String(a.id) === String(id) ? updated : a));
      set({ agents: next });
      saveJson('teamone_support_agents', next);
      toast.success('Agent profile updated successfully');
      return updated;
    } catch (err: any) {
      const existing = get().agents.find((a) => String(a.id) === String(id));
      const updated = normalizeAgent({ ...existing, ...updates, id });
      const next = get().agents.map((a) => (String(a.id) === String(id) ? updated : a));
      set({ agents: next });
      saveJson('teamone_support_agents', next);
      toast.success('Agent profile updated');
      return updated;
    }
  },

  deleteAgent: async (id) => {
    try {
      await apiClient.delete(`/support/agents/${id}`);
    } catch (err) {
      console.warn('Backend delete agent fallback:', err);
    }
    const next = get().agents.filter((a) => String(a.id) !== String(id));
    set({ agents: next });
    saveJson('teamone_support_agents', next);
    toast.success('Agent removed from support directory');
  },

  // --- AUTO RULES ---
  addRule: async (data) => {
    const newRule: AutoRule = {
      ...data,
      id: `rule-${Date.now()}`,
      created_at: new Date().toISOString(),
    };
    const next = [...get().autoRules, newRule];
    set({ autoRules: next });
    saveJson('teamone_support_auto_rules', next);
    toast.success(`Routing rule "${newRule.name}" created`);
    return newRule;
  },

  updateRule: async (id, updates) => {
    const next = get().autoRules.map((r) => (String(r.id) === String(id) ? { ...r, ...updates } : r));
    set({ autoRules: next });
    saveJson('teamone_support_auto_rules', next);
    toast.success('Routing rule updated');
  },

  deleteRule: async (id) => {
    const next = get().autoRules.filter((r) => String(r.id) !== String(id));
    set({ autoRules: next });
    saveJson('teamone_support_auto_rules', next);
    toast.success('Routing rule removed');
  },

  // --- SLA POLICIES ---
  fetchSlaPolicies: async () => {
    try {
      const res = await apiClient.get('/support/sla/policies');
      const data = res.data?.data || res.data;
      if (Array.isArray(data) && data.length > 0) {
        set({ slaPolicies: data });
        saveJson('teamone_support_sla_policies', data);
      }
    } catch (err) {
      console.warn('Failed to fetch SLA policies:', err);
    }
  },
  addPolicy: async (data) => {
    const newPolicy: SLAPolicy = {
      ...data,
      id: `sla-${Date.now()}`,
    };
    const next = [...get().slaPolicies, newPolicy];
    set({ slaPolicies: next });
    saveJson('teamone_support_sla_policies', next);
    toast.success(`SLA Policy for ${newPolicy.priority} created`);
    return newPolicy;
  },

  updatePolicy: async (id, updates) => {
    const next = get().slaPolicies.map((p) => (p.id === id ? { ...p, ...updates } : p));
    set({ slaPolicies: next });
    saveJson('teamone_support_sla_policies', next);
    toast.success('SLA Policy updated');
  },

  deletePolicy: async (id) => {
    const next = get().slaPolicies.filter((p) => p.id !== id);
    set({ slaPolicies: next });
    saveJson('teamone_support_sla_policies', next);
    toast.success('SLA Policy removed');
  },

  // --- NUMBERING ---
  updateNumbering: async (updates) => {
    const next = { ...get().numberingConfig, ...updates };
    set({ numberingConfig: next });
    saveJson('teamone_support_numbering_config', next);
    toast.success('Ticket ID numbering format updated');
  },

  generateNextTicketId: () => {
    const { prefix, padding_digits, next_number, include_year, separator } = get().numberingConfig;
    const yearStr = include_year ? `${new Date().getFullYear()}` : '';
    const paddedNum = String(next_number).padStart(padding_digits, '0');
    return `${prefix}${separator}${yearStr ? yearStr + '-' : ''}${paddedNum}`;
  },

  // --- EMAIL CONFIG ---
  updateEmailConfig: async (updates) => {
    const next = { ...get().emailConfig, ...updates };
    set({ emailConfig: next });
    saveJson('teamone_support_email_config', next);
    toast.success('Support email configuration updated');
  },

  testEmailConnection: async () => {
    const toastId = toast.loading('Pinging mail gateway...');
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success('Email gateway connection verified!', { id: toastId });
      return true;
    } catch {
      toast.error('Email connection failed', { id: toastId });
      return false;
    }
  },

  // --- TEMPLATES ---
  addTemplate: async (data) => {
    const newTmpl: EmailTemplate = {
      ...data,
      id: `tmpl-${Date.now()}`,
      created_at: new Date().toISOString(),
    };
    const next = [...get().emailTemplates, newTmpl];
    set({ emailTemplates: next });
    saveJson('teamone_support_email_templates', next);
    toast.success(`Template "${newTmpl.name}" saved`);
    return newTmpl;
  },

  updateTemplate: async (id, updates) => {
    const next = get().emailTemplates.map((t) => (t.id === id ? { ...t, ...updates } : t));
    set({ emailTemplates: next });
    saveJson('teamone_support_email_templates', next);
    toast.success('Template updated');
  },

  deleteTemplate: async (id) => {
    const next = get().emailTemplates.filter((t) => t.id !== id);
    set({ emailTemplates: next });
    saveJson('teamone_support_email_templates', next);
    toast.success('Template removed');
  },

  // --- DB CONFIG ---
  updateDBConfig: (updates) => {
    const next = { ...get().dbConfig, ...updates };
    set({ dbConfig: next });
  },

  fetchDatabaseConfig: async () => {
    try {
      const res = await apiClient.get('/support/settings/database-config');
      const data = res.data?.data || res.data;
      if (data) {
        set({
          dbConfig: {
            ...get().dbConfig,
            engine: data.engine || 'PostgreSQL',
            host: data.host || '',
            port: data.port || 5432,
            database_name: data.database_name || '',
            username: data.username || '',
            password: '',
            hasPassword: Boolean(data.hasPassword),
            ssl_mode: data.ssl_mode || 'prefer',
            max_pool: data.max_pool || 20,
            idle_timeout_ms: data.idle_timeout_ms || 30000,
            connection_timeout_ms: data.connection_timeout_ms || 5000,
            is_primary: data.is_primary ?? true,
            is_active: data.is_active ?? true,
            read_replica_host: data.read_replica_host || '',
            read_replica_port: data.read_replica_port || '',
            backup_schedule: data.backup_schedule || 'daily',
            is_configured: Boolean(data.is_configured),
            status: data.status || 'Not Configured',
          },
        });
      }
    } catch (err) {
      console.warn('Failed to fetch database configuration:', err);
    }
  },

  saveDatabaseConfig: async (configUpdates) => {
    const target = { ...get().dbConfig, ...(configUpdates || {}) };
    try {
      const res = await apiClient.put('/support/settings/database-config', target);
      const data = res.data?.data || res.data;
      const merged = { ...target, ...(data || {}), password: '' };
      set({ dbConfig: merged });
      toast.success('Database configuration saved to PostgreSQL support schema');
    } catch (err: any) {
      toast.error(`Failed to save database configuration: ${err.response?.data?.error || err.message}`);
      throw err;
    }
  },

  testDBConnection: async (overrideConfig) => {
    const target = { ...get().dbConfig, ...(overrideConfig || {}) };
    set({ isTestingDatabase: true, databaseTestResult: null });
    const toastId = toast.loading(`Testing connection to ${target.engine || 'database'}...`);
    try {
      const res = await apiClient.post('/support/settings/database-config/test', target);
      const data = res.data;
      set({
        isTestingDatabase: false,
        databaseTestResult: {
          success: true,
          message: data.message || `Connected to ${target.engine} successfully!`,
          latencyMs: data.latencyMs,
          version: data.version,
          engine: data.engine,
        },
      });
      toast.success(data.message || 'Database verified successfully!', { id: toastId });
      return true;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Database connection test failed';
      set({
        isTestingDatabase: false,
        databaseTestResult: {
          success: false,
          message: errorMsg,
          engine: target.engine,
        },
      });
      toast.error(errorMsg, { id: toastId });
      return false;
    }
  },

  // --- AUDIT LOGS ---
  logAuditEvent: (event) => {
    const newEvent: AuditLogEvent = {
      ...event,
      id: `aud-${Date.now()}`,
      timestamp: new Date().toISOString(),
    };
    const next = [newEvent, ...get().auditLogs.slice(0, 100)];
    set({ auditLogs: next });
    saveJson('teamone_support_audit_logs', next);
  },

  addAuditLog: (event) => {
    get().logAuditEvent(event);
  },

  clearAuditLogs: () => {
    set({ auditLogs: [] });
    saveJson('teamone_support_audit_logs', []);
    toast.success('Audit trail cleared');
  },

  // --- S3 STORAGE CONFIG ---
  updateStorageConfig: (updates) => {
    const next = { ...get().storageConfig, ...updates, provider: 's3' as const };
    set({ storageConfig: next });
    saveJson('teamone_support_storage_config', next);
  },

  fetchS3Settings: async () => {
    set({ isLoadingS3Settings: true });
    try {
      let res;
      try {
        res = await apiClient.get('/support/settings/storage');
      } catch {
        res = await apiClient.get('/settings/storage');
      }
      const data = res.data?.data || res.data;
      if (data) {
        set({
          s3Settings: data,
          isLoadingS3Settings: false,
          storageConfig: {
            ...get().storageConfig,
            provider: 's3',
            s3_bucket: data.bucket_name || '',
            s3_region: data.region || 'ap-south-1',
            s3_prefix: data.key_prefix || 'support/',
            s3_endpoint: data.endpoint_url || '',
            force_path_style: data.force_path_style || false,
            access_key_id: data.aws_access_key_id || '',
            use_iam_role: data.use_iam_role || false,
            sse_mode: data.sse_mode || 'AES256',
            kms_key_id: data.kms_key_id || '',
            presign_upload_expiry_seconds: data.presign_upload_expiry_seconds || 300,
            presign_download_expiry_seconds: data.presign_download_expiry_seconds || 300,
            max_file_size_mb: data.max_file_size_mb || 25,
            allowed_file_types: data.allowed_mime_types || get().storageConfig.allowed_file_types,
            mirror_ticket_json: data.mirror_ticket_json !== false,
            is_configured: Boolean(data.bucket_name && (data.use_iam_role || data.aws_secret_access_key_set)),
            access_key_preview: data.aws_access_key_id ? `${data.aws_access_key_id.slice(0, 4)}...${data.aws_access_key_id.slice(-4)}` : '',
          },
        });
        return data;
      }
      set({ isLoadingS3Settings: false });
      return null;
    } catch (err: any) {
      set({ isLoadingS3Settings: false });
      console.warn('Failed to fetch AWS S3 settings:', err);
      return null;
    }
  },

  updateS3Settings: async (updates) => {
    set({ isSavingS3Settings: true });
    try {
      let res;
      try {
        res = await apiClient.put('/support/settings/storage', updates);
      } catch {
        res = await apiClient.put('/settings/storage', updates);
      }
      const data = res.data?.data || res.data;
      if (data) {
        set({
          s3Settings: data,
          isSavingS3Settings: false,
          storageConfig: {
            ...get().storageConfig,
            provider: 's3',
            s3_bucket: data.bucket_name || '',
            s3_region: data.region || 'ap-south-1',
            s3_prefix: data.key_prefix || 'support/',
            s3_endpoint: data.endpoint_url || '',
            force_path_style: data.force_path_style || false,
            access_key_id: data.aws_access_key_id || '',
            use_iam_role: data.use_iam_role || false,
            sse_mode: data.sse_mode || 'AES256',
            kms_key_id: data.kms_key_id || '',
            presign_upload_expiry_seconds: data.presign_upload_expiry_seconds || 300,
            presign_download_expiry_seconds: data.presign_download_expiry_seconds || 300,
            max_file_size_mb: data.max_file_size_mb || 25,
            allowed_file_types: data.allowed_mime_types || get().storageConfig.allowed_file_types,
            mirror_ticket_json: data.mirror_ticket_json !== false,
            is_configured: Boolean(data.bucket_name && (data.use_iam_role || data.aws_secret_access_key_set)),
          },
        });
        toast.success('AWS S3 Cloud Vault settings saved successfully');
        return { success: true, data };
      }
      set({ isSavingS3Settings: false });
      return { success: true };
    } catch (err: any) {
      set({ isSavingS3Settings: false });
      const msg = err.response?.data?.message || err.message || 'Failed to update S3 settings';
      toast.error(msg);
      return { success: false, message: msg };
    }
  },

  testS3Connection: async (testPayload) => {
    set({ isTestingS3Connection: true, s3TestResult: null });
    try {
      let res;
      try {
        res = await apiClient.post('/support/settings/storage/test', testPayload || {});
      } catch (e1: any) {
        if (e1.response?.data?.steps) {
          res = e1.response;
        } else {
          res = await apiClient.post('/settings/storage/test', testPayload || {});
        }
      }
      const data = res.data;
      set({ isTestingS3Connection: false, s3TestResult: data });
      if (data.success) {
        toast.success(data.message || 'AWS S3 Cloud Vault connection verified!');
      } else {
        toast.error(data.message || 'S3 Connection Test Failed');
      }
      return data;
    } catch (err: any) {
      const errorResult = err.response?.data || {
        success: false,
        message: err.message || 'Failed to ping S3 endpoint',
        steps: {
          reachable: { ok: false, error: err.message },
          write: { ok: false },
          read: { ok: false },
          delete: { ok: false },
        },
      };
      set({ isTestingS3Connection: false, s3TestResult: errorResult });
      toast.error(errorResult.message || 'S3 Connection Test Failed');
      return errorResult;
    }
  },

  fetchStorageConfig: async () => {
    await get().fetchS3Settings();
  },

  saveStorageConfig: async (configUpdates) => {
    const s3Updates: any = {
      bucket_name: configUpdates?.s3_bucket,
      region: configUpdates?.s3_region,
      key_prefix: configUpdates?.s3_prefix,
      endpoint_url: configUpdates?.s3_endpoint,
      force_path_style: configUpdates?.force_path_style,
      aws_access_key_id: configUpdates?.access_key_id,
      aws_secret_access_key: configUpdates?.secret_access_key,
      use_iam_role: configUpdates?.use_iam_role,
      sse_mode: configUpdates?.sse_mode,
      kms_key_id: configUpdates?.kms_key_id,
      presign_upload_expiry_seconds: configUpdates?.presign_upload_expiry_seconds,
      presign_download_expiry_seconds: configUpdates?.presign_download_expiry_seconds,
      max_file_size_mb: configUpdates?.max_file_size_mb,
      allowed_mime_types: configUpdates?.allowed_file_types,
      mirror_ticket_json: configUpdates?.mirror_ticket_json,
    };
    await get().updateS3Settings(s3Updates);
  },

  testStorageConnection: async () => {
    set({ isTestingStorage: true, storageTestResult: null });
    const res = await get().testS3Connection();
    set({ isTestingStorage: false, storageTestResult: res });
    return res;
  },

  testMailuConnection: async (config?: any) => {
    return { success: true, message: 'Mailu daemon connected successfully', latency: 45 };
  },

  // --- OUTBOUND WEBHOOKS & CHAT SPACES ---
  fetchWebhooks: async () => {
    try {
      const res = await apiClient.get('/support/webhooks');
      const data = res.data?.data || res.data;
      if (Array.isArray(data)) {
        set({ webhooks: data });
        saveJson('teamone_support_webhooks_config', data);
      }
    } catch {}
  },

  addWebhook: async (data) => {
    const newWh: WebhookConfig = {
      ...data,
      id: `wh-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    try {
      const res = await apiClient.post('/support/webhooks', data);
      const created = res.data?.data || newWh;
      const next = [created, ...get().webhooks.filter((w) => w.id !== created.id)];
      set({ webhooks: next });
      saveJson('teamone_support_webhooks_config', next);
      toast.success(`Webhook "${created.name}" registered`);
      return created;
    } catch {
      const next = [newWh, ...get().webhooks];
      set({ webhooks: next });
      saveJson('teamone_support_webhooks_config', next);
      toast.success(`Webhook "${newWh.name}" saved`);
      return newWh;
    }
  },

  updateWebhook: async (id, updates) => {
    try {
      await apiClient.put(`/support/webhooks/${id}`, updates);
    } catch {}
    const next = get().webhooks.map((w) =>
      w.id === id ? { ...w, ...updates, updatedAt: new Date().toISOString() } : w
    );
    set({ webhooks: next });
    saveJson('teamone_support_webhooks_config', next);
    toast.success('Webhook updated');
  },

  deleteWebhook: async (id) => {
    try {
      await apiClient.delete(`/support/webhooks/${id}`);
    } catch {}
    const next = get().webhooks.filter((w) => w.id !== id);
    set({ webhooks: next });
    saveJson('teamone_support_webhooks_config', next);
    toast.success('Webhook removed');
  },

  fetchWebhookLogs: async () => {
    try {
      const res = await apiClient.get('/support/webhooks/logs');
      const data = res.data?.data || res.data;
      if (Array.isArray(data)) {
        set({ webhookLogs: data });
      }
    } catch {}
  },

  testWebhookEndpoint: async (webhook, event = 'ticket.sla_breach') => {
    set({ isTestingWebhook: true, webhookTestResult: null });
    try {
      const res = await apiClient.post('/support/webhooks/test', { webhook, event });
      const data = res.data;
      set({ isTestingWebhook: false, webhookTestResult: data });
      if (data?.success) {
        toast.success(data.message || 'Webhook delivered successfully!');
      } else {
        toast.error(data?.message || 'Webhook test failed');
      }
      void get().fetchWebhookLogs();
      return data;
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to ping webhook URL';
      const result = { success: false, message: errorMsg };
      set({ isTestingWebhook: false, webhookTestResult: result });
      toast.error(errorMsg);
      return result;
    }
  },

  triggerSampleWebhookEvent: async (event, data) => {
    try {
      const res = await apiClient.post('/support/webhooks/trigger-sample', { event, data });
      toast.success(`Dispatched sample ${event} notification!`);
      void get().fetchWebhookLogs();
      return res.data;
    } catch (err: any) {
      toast.error(`Sample dispatch failed: ${err.message || String(err)}`);
    }
  },

  // --- AWS SES EMAIL INFRASTRUCTURE ---
  fetchSesSettings: async () => {
    set({ isLoadingSesSettings: true });
    try {
      const res = await apiClient.get('/support/settings/email');
      const data = res.data?.data || res.data;
      set({ sesSettings: data, isLoadingSesSettings: false });
      return data;
    } catch (err: any) {
      set({ isLoadingSesSettings: false });
      console.warn('Failed to load SES email settings:', err);
      return null;
    }
  },

  updateSesSettings: async (data) => {
    set({ isSavingSesSettings: true });
    try {
      const res = await apiClient.put('/support/settings/email', data);
      const updated = res.data?.data || res.data;
      set({ sesSettings: updated, isSavingSesSettings: false });
      toast.success(res.data?.message || 'Email infrastructure settings updated successfully');
      return { success: true, data: updated };
    } catch (err: any) {
      set({ isSavingSesSettings: false });
      const msg = err.response?.data?.message || err.message || 'Failed to update email settings';
      toast.error(msg);
      return { success: false, message: msg };
    }
  },

  testSesConnection: async (data) => {
    set({ isTestingSesConnection: true });
    try {
      const res = await apiClient.post('/support/settings/email/test', data || {});
      set({ isTestingSesConnection: false });
      const msg = res.data?.message || 'Connection test successful';
      toast.success(msg);
      return { success: true, message: msg };
    } catch (err: any) {
      set({ isTestingSesConnection: false });
      const msg = err.response?.data?.message || err.message || 'Connection test failed';
      toast.error(msg);
      return { success: false, message: msg };
    }
  },

  checkInboundMailNow: async () => {
    set({ isSyncingEmail: true });
    try {
      const res = await apiClient.post('/support/settings/email/check-inbound');
      set({ isSyncingEmail: false });
      const msg = res.data?.message || 'Inbound mail checked';
      toast.success(msg);
      return { success: true, message: msg, count: res.data?.count };
    } catch (err: any) {
      set({ isSyncingEmail: false });
      const msg = err.response?.data?.message || err.message || 'Inbound check failed';
      toast.error(msg);
      return { success: false, message: msg };
    }
  },

  // --- EMAIL ACCOUNTS & INFRASTRUCTURE ---
  fetchEmailAccounts: async () => {
    try {
      const res = await apiClient.get('/support/settings/email-accounts');
      const data = res.data?.data || res.data;
      if (Array.isArray(data)) {
        set({ emailAccounts: data });
      }
    } catch (err) {
      console.warn('Failed to load email accounts:', err);
    }
  },

  saveEmailAccount: async (account) => {
    try {
      const res = await apiClient.post('/support/settings/email-accounts', account);
      const data = res.data?.data || res.data;
      toast.success(`Email account "${account.email_address || account.name}" saved`);
      await get().fetchEmailAccounts();
      return data;
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to save email account';
      toast.error(msg);
      return null;
    }
  },

  deleteEmailAccount: async (id) => {
    try {
      await apiClient.delete(`/support/settings/email-accounts/${id}`);
      set({ emailAccounts: get().emailAccounts.filter((a) => a.id !== id) });
      toast.success('Email account removed');
    } catch {
      toast.error('Failed to remove email account');
    }
  },

  syncEmailAccounts: async (accountId?: string) => {
    set({ isSyncingEmail: true });
    try {
      const url = accountId
        ? `/support/settings/email-accounts/${accountId}/sync`
        : `/support/settings/email-accounts/sync`;
      const res = await apiClient.post(url);
      const data = res.data;
      set({ isSyncingEmail: false });
      if (data?.success) {
        toast.success(data.message || 'Mailbox checked successfully!');
      } else {
        toast.error(data?.message || 'Mailbox sync encountered an error');
      }
      await get().fetchEmailAccounts();
      await get().fetchEmailLogs();
      return data;
    } catch (err: any) {
      set({ isSyncingEmail: false });
      const msg = err.response?.data?.message || err.message || 'Failed to sync mailbox';
      toast.error(msg);
      return { success: false, message: msg };
    }
  },

  fetchEmailLogs: async (_limit = 50) => {
    try {
      const res = await apiClient.get('/support/settings/email-logs');
      const data = res.data?.data || res.data;
      if (Array.isArray(data)) {
        set({ emailProcessingLogs: data });
      }
    } catch (err) {
      console.warn('Failed to fetch email logs:', err);
    }
  },

  fetchDnsChecklist: async (subdomain = 'support.trustybytes.in', hostname = 'mail.support.trustybytes.in') => {
    try {
      const res = await apiClient.get(`/support/email-infrastructure/dns-checklist?subdomain=${encodeURIComponent(subdomain)}&hostname=${encodeURIComponent(hostname)}`);
      const data = res.data?.data || res.data;
      set({ dnsChecklist: data });
      return data;
    } catch (err: any) {
      console.warn('Failed to fetch DNS checklist:', err);
      return null;
    }
  },

  simulateIncomingEmail: async (payload) => {
    try {
      const res = await apiClient.post('/support/email-infrastructure/simulate-inbound', payload);
      const data = res.data;
      if (data?.success) {
        toast.success(data.message || 'Incoming email processed successfully!');
      } else {
        toast.error(data?.message || 'Email processing failed');
      }
      void get().fetchEmailLogs();
      return data;
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Error simulating email inbound';
      toast.error(msg);
      return { success: false, message: msg };
    }
  },

  testEmailInfraConnection: async (config) => {
    set({ isTestingEmailInfra: true, emailInfraTestResult: null });
    try {
      const res = await apiClient.post('/support/email-infrastructure/test-connection', config);
      const data = res.data;
      set({ isTestingEmailInfra: false, emailInfraTestResult: data });
      if (data?.success) {
        toast.success(data.message || 'Email Connection Verified!');
      } else {
        toast.error(data?.message || 'Connection test returned errors');
      }
      return data;
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to ping mail services';
      const result = { success: false, message: msg };
      set({ isTestingEmailInfra: false, emailInfraTestResult: result });
      toast.error(msg);
      return result;
    }
  },

  // ─────────────────────────────────────────────────────────────────
  // 13. Dynamic Ticket Types
  // ─────────────────────────────────────────────────────────────────
  fetchTicketTypes: async () => {
    set({ isLoadingTicketTypes: true });
    try {
      const res = await apiClient.get('/support/settings/ticket-types');
      const data = res.data?.data || res.data;
      if (Array.isArray(data) && data.length > 0) {
        const merged = data.map((tt: any) => {
          const isHr = String(tt.name || '').toLowerCase().includes('hr') ||
            String(tt.code || '').toLowerCase().startsWith('hr') ||
            String(tt.teamName || '').toLowerCase().includes('hr') ||
            ['leave', 'onboarding', 'offboarding', 'payroll'].some((k) => String(tt.name || '').toLowerCase().includes(k));

          let flags: any = {};
          try {
            const rawFlags = localStorage.getItem(`teamone_ticket_type_flags_${tt.id}`) ||
              (tt.code ? localStorage.getItem(`teamone_ticket_type_flags_${tt.code.toLowerCase()}`) : null);
            if (rawFlags) flags = JSON.parse(rawFlags);
          } catch {}

          return {
            ...tt,
            enableAsset: flags.enableAsset !== undefined ? flags.enableAsset : (tt.enableAsset !== undefined ? tt.enableAsset : !isHr),
            enableVendor: flags.enableVendor !== undefined ? flags.enableVendor : (tt.enableVendor !== undefined ? tt.enableVendor : !isHr),
            enableSla: flags.enableSla !== undefined ? flags.enableSla : (tt.enableSla !== undefined ? tt.enableSla : true),
          };
        });

        set({ ticketTypes: merged, isLoadingTicketTypes: false });
        try {
          localStorage.setItem('teamone_ticket_types_config', JSON.stringify(merged));
        } catch {}
      } else {
        set({ isLoadingTicketTypes: false });
      }
    } catch (err) {
      console.warn('Failed to fetch ticket types:', err);
      set({ isLoadingTicketTypes: false });
    }
  },

  saveTicketType: async (typeData: TicketTypeConfig) => {
    try {
      const res = await apiClient.post('/support/settings/ticket-types', typeData);
      const saved = res.data?.data || typeData;
      const mergedSaved: TicketTypeConfig = {
        ...typeData,
        ...saved,
        enableAsset: typeData.enableAsset,
        enableVendor: typeData.enableVendor,
        enableSla: typeData.enableSla,
      };
      const updatedTypes = [
        mergedSaved,
        ...get().ticketTypes.filter((t) => t.id !== mergedSaved.id),
      ].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      set({ ticketTypes: updatedTypes });
      try {
        localStorage.setItem('teamone_ticket_types_config', JSON.stringify(updatedTypes));
        const flags = {
          enableAsset: mergedSaved.enableAsset,
          enableVendor: mergedSaved.enableVendor,
          enableSla: mergedSaved.enableSla,
        };
        localStorage.setItem(`teamone_ticket_type_flags_${mergedSaved.id}`, JSON.stringify(flags));
        if (mergedSaved.code) {
          localStorage.setItem(`teamone_ticket_type_flags_${mergedSaved.code.toLowerCase()}`, JSON.stringify(flags));
        }
      } catch {}
      toast.success(`Ticket type "${mergedSaved.name}" saved`);
      return mergedSaved;
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to save ticket type';
      toast.error(msg);
      throw err;
    }
  },

  deleteTicketType: async (id: string) => {
    try {
      await apiClient.delete(`/support/settings/ticket-types/${id}`);
      set({ ticketTypes: get().ticketTypes.filter((t) => t.id !== id) });
      toast.success('Ticket type deleted');
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to delete ticket type';
      toast.error(msg);
      throw err;
    }
  },

  // ─────────────────────────────────────────────────────────────────
  // 13.1 Categories, Impacts, Urgencies & End-Users from DB
  // ─────────────────────────────────────────────────────────────────
  fetchCategories: async () => {
    try {
      const res = await apiClient.get('/support/settings/categories');
      const data = res.data?.data || res.data;
      if (Array.isArray(data) && data.length > 0) {
        set({ ticketCategories: data });
      }
    } catch (err) {
      console.warn('Failed to fetch categories:', err);
    }
  },

  fetchImpacts: async () => {
    try {
      const res = await apiClient.get('/support/settings/impacts');
      const data = res.data?.data || res.data;
      if (Array.isArray(data) && data.length > 0) {
        set({ ticketImpacts: data });
      }
    } catch (err) {
      console.warn('Failed to fetch impacts:', err);
    }
  },

  fetchUrgencies: async () => {
    try {
      const res = await apiClient.get('/support/settings/urgencies');
      const data = res.data?.data || res.data;
      if (Array.isArray(data) && data.length > 0) {
        set({ ticketUrgencies: data });
      }
    } catch (err) {
      console.warn('Failed to fetch urgencies:', err);
    }
  },

  fetchEndUsers: async () => {
    try {
      const res = await apiClient.get('/support/settings/end-users');
      const data = res.data?.data || res.data;
      if (Array.isArray(data) && data.length > 0) {
        set({ endUsers: data });
      }
    } catch (err) {
      console.warn('Failed to fetch end users:', err);
    }
  },

  // ─────────────────────────────────────────────────────────────────
  // 14. Ticket Actions
  // ─────────────────────────────────────────────────────────────────
  fetchTicketActions: async () => {
    set({ isLoadingTicketActions: true });
    try {
      const res = await apiClient.get('/support/settings/actions');
      const data = res.data?.data || res.data;
      if (Array.isArray(data) && data.length > 0) {
        set({ ticketActions: data, isLoadingTicketActions: false });
        try {
          localStorage.setItem('teamone_ticket_actions_config', JSON.stringify(data));
        } catch {}
      } else {
        set({ isLoadingTicketActions: false });
      }
    } catch (err) {
      console.warn('Failed to fetch ticket actions:', err);
      set({ isLoadingTicketActions: false });
    }
  },

  saveTicketAction: async (actionData: TicketActionConfig) => {
    try {
      const res = await apiClient.post('/support/settings/actions', actionData);
      const saved = res.data?.data || actionData;
      set({
        ticketActions: [
          saved,
          ...get().ticketActions.filter((a) => a.id !== saved.id),
        ].sort((a, b) => ((a.outcomeDescription || a.buttonName || '')).localeCompare(b.outcomeDescription || b.buttonName || '')),
      });
      toast.success(`Action "${saved.buttonName}" saved`);
      return saved;
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to save ticket action';
      toast.error(msg);
      throw err;
    }
  },

  deleteTicketAction: async (id: string) => {
    try {
      await apiClient.delete(`/support/settings/actions/${id}`);
      set({ ticketActions: get().ticketActions.filter((a) => a.id !== id) });
      toast.success('Ticket action deleted');
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to delete ticket action';
      toast.error(msg);
      throw err;
    }
  },

  // ─────────────────────────────────────────────────────────────────
  // 15. Extended Workflows
  // ─────────────────────────────────────────────────────────────────
  fetchExtendedWorkflows: async () => {
    set({ isLoadingWorkflows: true });
    try {
      const res = await apiClient.get('/support/settings/workflows');
      const data = res.data?.data || res.data;
      if (Array.isArray(data) && data.length > 0) {
        set({ extendedWorkflows: data, isLoadingWorkflows: false });
        try {
          localStorage.setItem('teamone_extended_workflows', JSON.stringify(data));
        } catch {}
      } else {
        set({ isLoadingWorkflows: false });
      }
    } catch (err) {
      console.warn('Failed to fetch workflows:', err);
      set({ isLoadingWorkflows: false });
    }
  },

  saveExtendedWorkflow: async (wfData: ExtendedWorkflow) => {
    try {
      const res = await apiClient.post('/support/settings/workflows', wfData);
      const saved = res.data?.data || wfData;
      const updatedList = [
        saved,
        ...get().extendedWorkflows.filter((w) => String(w.id) !== String(saved.id) && String(w.id) !== String(wfData.id)),
      ].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      set({ extendedWorkflows: updatedList });
      try {
        localStorage.setItem('teamone_extended_workflows', JSON.stringify(updatedList));
      } catch {}
      toast.success(`Workflow "${saved.name}" saved`);
      return saved;
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to save workflow';
      toast.error(msg);
      throw err;
    }
  },

  deleteExtendedWorkflow: async (id: string) => {
    try {
      await apiClient.delete(`/support/settings/workflows/${id}`);
      set({ extendedWorkflows: get().extendedWorkflows.filter((w) => w.id !== id) });
      toast.success('Workflow deleted');
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to delete workflow';
      toast.error(msg);
      throw err;
    }
  },

  deleteWorkflowStage: async (workflowId: string, stageId: string) => {
    try {
      if (!isNaN(Number(stageId)) && Number(stageId) > 0) {
        await apiClient.delete(`/support/settings/workflows/${workflowId}/stages/${stageId}`).catch(() => {});
      }
    } catch (err: any) {
      console.warn('Failed to delete stage on server:', err);
    }
  },

  fetchMasterTicketTypeWorkflows: async (ticketTypeId?: string) => {
    try {
      const url = ticketTypeId
        ? `/support/settings/master/ticket-type-workflows?ticketTypeId=${encodeURIComponent(ticketTypeId)}`
        : '/support/settings/master/ticket-type-workflows';
      const res = await apiClient.get(url);
      const data = res.data?.data || res.data;
      if (Array.isArray(data)) {
        set({ masterTicketTypeWorkflows: data });
        return data;
      }
      return [];
    } catch (err) {
      console.warn('Failed to fetch master ticket type workflows:', err);
      return [];
    }
  },


  // ─────────────────────────────────────────────────────────────────
  // 17. Notification Rules & Preferences
  // ─────────────────────────────────────────────────────────────────
  fetchNotificationRules: async () => {
    set({ isLoadingNotificationRules: true });
    try {
      const res = await apiClient.get('/support/settings/notifications');
      const data = res.data?.data || res.data;
      if (Array.isArray(data) && data.length > 0) {
        set({ notificationRules: data, isLoadingNotificationRules: false });
      } else {
        set({ isLoadingNotificationRules: false });
      }
    } catch (err) {
      console.warn('Failed to fetch notification rules:', err);
      set({ isLoadingNotificationRules: false });
    }
  },

  saveNotificationRule: async (ruleData: NotificationRule) => {
    try {
      const res = await apiClient.post('/support/settings/notifications', ruleData);
      const saved = res.data?.data || ruleData;
      set({
        notificationRules: [
          saved,
          ...get().notificationRules.filter((r) => r.id !== saved.id),
        ].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      });
      toast.success(`Notification rule "${saved.name}" saved`);
      return saved;
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to save notification rule';
      toast.error(msg);
      throw err;
    }
  },

  deleteNotificationRule: async (id: string) => {
    try {
      await apiClient.delete(`/support/settings/notifications/${id}`);
      set({ notificationRules: get().notificationRules.filter((r) => r.id !== id) });
      toast.success('Notification rule deleted');
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to delete notification rule';
      toast.error(msg);
      throw err;
    }
  },

  fetchAgentPreference: async (email?: string) => {
    try {
      const res = await apiClient.get(`/support/settings/notifications/preferences${email ? `?email=${encodeURIComponent(email)}` : ''}`);
      const data = res.data?.data || res.data;
      if (data) {
        set({ agentPreference: data });
        return data;
      }
      return { agentEmail: email || '', agentName: '', subscribedRuleIds: [], optedOutChannelMap: {} };
    } catch (err) {
      console.warn('Failed to fetch agent preference:', err);
      return { agentEmail: email || '', agentName: '', subscribedRuleIds: [], optedOutChannelMap: {} };
    }
  },

  saveAgentPreference: async (prefData: PersonalAgentPreference) => {
    try {
      const res = await apiClient.post('/support/settings/notifications/preferences', prefData);
      const saved = res.data?.data || prefData;
      set({ agentPreference: saved });
      toast.success('Agent notification preferences updated');
      return saved;
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to save agent preferences';
      toast.error(msg);
      throw err;
    }
  },

  // ─────────────────────────────────────────────────────────────────
  // 18. Mail Providers
  // ─────────────────────────────────────────────────────────────────
  fetchMailProviders: async () => {
    try {
      const res = await apiClient.get('/support/settings/mail-providers');
      const data = res.data?.data || res.data;
      if (Array.isArray(data) && data.length > 0) {
        set({ mailProviders: data });
      }
    } catch (err) {
      console.warn('Failed to fetch mail providers:', err);
    }
  },

  saveMailProvider: async (providerData: any) => {
    try {
      const res = await apiClient.post('/support/settings/mail-providers', providerData);
      const saved = res.data?.data || providerData;
      set({
        mailProviders: [
          saved,
          ...get().mailProviders.filter((p) => p.id !== saved.id),
        ],
      });
      toast.success('Mail provider settings updated');
      return saved;
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to save mail provider';
      toast.error(msg);
      throw err;
    }
  },

  resetAllSettingsToDefaults: () => {
    set({
      queues: INITIAL_QUEUES,
      agents: INITIAL_AGENTS,
      autoRules: INITIAL_RULES,
      slaPolicies: INITIAL_SLA,
      numberingConfig: INITIAL_NUMBERING,
      emailConfig: INITIAL_EMAIL_CONFIG,
      emailTemplates: INITIAL_TEMPLATES,
      dbConfig: INITIAL_DB_CONFIG,
    });
    saveJson('teamone_support_queues', INITIAL_QUEUES);
    saveJson('teamone_support_agents', INITIAL_AGENTS);
    saveJson('teamone_support_auto_rules', INITIAL_RULES);
    saveJson('teamone_support_sla_policies', INITIAL_SLA);
    saveJson('teamone_support_numbering_config', INITIAL_NUMBERING);
    saveJson('teamone_support_email_config', INITIAL_EMAIL_CONFIG);
    saveJson('teamone_support_email_templates', INITIAL_TEMPLATES);
    saveJson('teamone_support_db_config', INITIAL_DB_CONFIG);
    toast.success('All support settings reset to defaults');
  },
}));

export interface ActiveSupportSender {
  name: string;
  email: string;
  formatted: string;
  isConfigured: boolean;
  displayName: string;
  displayFrom: string;
  badgeLabel: string;
}

export function getActiveSupportSender(state?: Partial<SupportSettingsState>): ActiveSupportSender {
  const current = state || useSupportSettingsStore.getState();
  const accounts = current.emailAccounts || [];

  // 1. Check configured default or active email account from Mail Infrastructure
  // First look for active accounts (is_active !== false)
  const activeAcc =
    accounts.find((a) => (a.is_default || (a as any).isDefault) && a.is_active !== false) ||
    accounts.find((a) => a.is_active === true) ||
    accounts.find((a) => a.is_active !== false);

  if (activeAcc) {
    const email = (activeAcc.email_address || (activeAcc as any).emailAddress || activeAcc.email || '').trim();
    const name = (activeAcc.name || 'TeamOne Support').trim();
    if (email && email.includes('@')) {
      const formatted = `${name} <${email}>`;
      return {
        name,
        displayName: name,
        email,
        formatted,
        displayFrom: formatted,
        isConfigured: true,
        badgeLabel: 'Primary Mail Infra',
      };
    }
  }

  // 2. Check active mail provider configuration
  const providers = current.mailProviders || [];
  const activeProv = providers.find((p: any) => p.isActive === true || p.is_active === true);
  if (activeProv) {
    const cfg = typeof activeProv.config === 'string' ? JSON.parse(activeProv.config) : activeProv.config || {};
    const email = (cfg.emailAddress || cfg.senderEmail || cfg.senderIdentity || cfg.delegationEmail || cfg.smtpUsername || '').trim();
    const name = (cfg.senderName || cfg.accountLabel || cfg.fromName || 'TeamOne Support').trim();
    if (email && email.includes('@')) {
      const formatted = `${name} <${email}>`;
      return {
        name,
        displayName: name,
        email,
        formatted,
        displayFrom: formatted,
        isConfigured: true,
        badgeLabel: 'Mail Provider Infra',
      };
    }
  }

  // 3. Check general email configuration
  const cfg = current.emailConfig;
  if (cfg) {
    let email = '';
    if (cfg.provider === 'aws_ses') {
      email = cfg.aws_ses_sender_email || '';
    } else if (cfg.provider === 'google') {
      email = cfg.google_delegation_email || '';
    } else if (cfg.provider === 'smtp_imap') {
      email = (cfg as any).emailAddress || cfg.smtp_username || cfg.inbound_imap_username || '';
    }
    email = email.trim();
    const name = ((cfg as any).senderName || (cfg as any).fromName || 'TeamOne Support').trim();
    if (email && email.includes('@')) {
      const formatted = `${name} <${email}>`;
      return {
        name,
        displayName: name,
        email,
        formatted,
        displayFrom: formatted,
        isConfigured: true,
        badgeLabel: 'Email Config',
      };
    }
  }

  // 4. If any account exists (even inactive default), check if it has a valid email address as fallback
  const fallbackAcc = accounts.find((a) => a.is_default || (a as any).isDefault) || accounts[0];
  if (fallbackAcc) {
    const email = (fallbackAcc.email_address || (fallbackAcc as any).emailAddress || fallbackAcc.email || '').trim();
    const name = (fallbackAcc.name || 'TeamOne Support').trim();
    if (email && email.includes('@')) {
      const formatted = `${name} <${email}>`;
      return {
        name,
        displayName: name,
        email,
        formatted,
        displayFrom: formatted,
        isConfigured: true,
        badgeLabel: 'Mail Infrastructure',
      };
    }
  }

  // 5. Not configured
  return {
    name: '',
    displayName: '',
    email: '',
    formatted: '',
    displayFrom: '',
    isConfigured: false,
    badgeLabel: 'Mail Not Configured',
  };
}

export function useActiveSupportSender(): ActiveSupportSender {
  const emailAccounts = useSupportSettingsStore((s) => s.emailAccounts);
  const mailProviders = useSupportSettingsStore((s) => s.mailProviders);
  const emailConfig = useSupportSettingsStore((s) => s.emailConfig);

  return React.useMemo(() => {
    return getActiveSupportSender({ emailAccounts, mailProviders, emailConfig });
  }, [emailAccounts, mailProviders, emailConfig]);
}

if (typeof window !== 'undefined') {
  setTimeout(() => {
    useSupportSettingsStore.getState().fetchEmailAccounts().catch(() => {});
    useSupportSettingsStore.getState().fetchMailProviders().catch(() => {});
  }, 50);
}

export * from '../lib/emailTemplateRenderer';

